import React, { useState, memo, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Heart, Eye, MessageCircle, Crown, Calendar, Clock, Hash, Gauge, Zap, Package, Wrench, Settings, Edit, Pause, Play, Trash2, Shield, Users, Check, Plane, FileCheck, DollarSign, Truck, Globe, Lock, Building2, User, GraduationCap, Award, FileText, Camera } from 'lucide-react';
import HelicopterSilhouetteIcon from '@/components/icons/HelicopterSilhouetteIcon';
import OptimizedImage from '@/components/OptimizedImage';
import SmartLogo from '@/components/SmartLogo';
import { cn } from '@/lib/utils';
import { useFavorites } from '@/hooks/useFavorites';
import ListingTypeBadge from '@/components/ListingTypeBadge';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatListingPrice, formatServiceListingPrice } from '@/utils/currencyConverter';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import heroPlaceholder from '@/assets/hero-helicopter-new.jpg';
import chopperSwapLogo from '@/assets/chopperswap-logo-no-bg.png';
import { ShareListingButton } from '@/components/ShareListingButton';
import { LifeIndicator } from './LifeIndicator';
import { EquipmentBadges } from './EquipmentBadges';
import { ConfigurationBadge } from './ConfigurationBadge';
import { BlurredPrivateData } from '@/components/privacy/BlurredPrivateData';
import { useEffectiveVisibility } from '@/hooks/useEffectiveVisibility';
import JobQualificationBadges from '@/components/jobs/JobQualificationBadges';
import ServiceCapabilityBadges from '@/components/services/ServiceCapabilityBadges';
import { getJobTypeMapPin, isJobOffering } from '@/utils/jobTypeIcons';
import { getServiceTypeMapPin, isServiceOffering } from '@/utils/serviceTypeIcons';
import { formatResponseTime, formatShiftCoverage } from '@/utils/serviceFormatting';
import { formatJobSalary } from '@/utils/formatJobSalary';
import { getServiceCategoryBadgeConfig } from '@/utils/serviceCategoryIcons';
import { useUnifiedContact } from '@/contexts/UnifiedContactContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const BrandedListingFallback: React.FC<{ className?: string; logoClassName?: string }> = ({
  className,
  logoClassName,
}) => (
  <div className={cn('flex h-full w-full items-center justify-center bg-card dark:bg-background', className)}>
    <img
      src={chopperSwapLogo}
      alt="ChopperSwap"
      className={cn('h-auto max-h-[58%] w-2/3 max-w-[72%] object-contain opacity-90 dark:brightness-0 dark:invert dark:opacity-95', logoClassName)}
    />
  </div>
);

export interface MarketplaceListingCardProps {
  // Core fields (all asset types)
  id: string;
  title: string;
  price?: number;
  location?: string;
  latitude?: number | null;
  longitude?: number | null;
  images?: string[];
  featured?: boolean;
  listingType?: string;
  assetType: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
  isBuyRequest?: boolean;
  condition?: string;
  year?: number;
  
  // Privacy/visibility settings
  visibilitySettings?: {
    use_global_defaults?: boolean;
    hide_price?: boolean;
    hide_location?: boolean;
    hide_serial?: boolean;
    hide_hours?: boolean;
    hide_contact?: boolean;
    hide_email?: boolean;
    hide_phone?: boolean;
    hide_company?: boolean;
    hide_modules?: boolean;
    hide_shipping?: boolean;
  };
  
  // Seller info
  sellerId?: string;
  sellerName?: string;
  
  // Helicopter-specific (operational data)
  helicopterData?: {
    manufacturer?: string;
    model?: string;
    // Operational specs (buyer-critical)
    airframeHours?: number;
    airframeLandings?: number | null; // Changed from airframeCycles
    airframeCycles?: number | null; // Deprecated - use airframeLandings
    engineHours?: number;
    engineCycles?: number;
    engineLifeRemaining?: number;
    engineLifeLimit?: number;
    mainRotorHours?: number;
    mainRotorLifeLimit?: number;
    // Configuration & equipment
    configurationName?: string;
    selectedEquipment?: string[];
    // Lease terms
    leaseMinPeriod?: number;
    leaseMaxPeriod?: number;
    // Wet lease specific
    includedServices?: string[];
    pilotProvided?: boolean;
    // Buy request fields
    urgencyLevel?: string;
    financingStatus?: string;
    maxAge?: number;
    maxFlightHours?: number;
    // Rate options
    rentalPeriod?: string;
    hourlyRate?: number;
    dailyRate?: number;
    monthlyRate?: number;
    // Operational requirements
    insuranceCapability?: string;
    pilotQualifications?: string[];
  };
  
  // Engine-specific
  engineData?: {
    // Core specs
    tsn?: number;
    csn?: number;
    tso?: number;
    cso?: number;
    tbo?: number;
    powerOutput?: number;
    powerUnit?: string;
    manufacturer?: string;
    model?: string;
    variant?: string;
    serialNumber?: string;
    // Rental fields
    rentalRate?: number;
    rentalPeriod?: string;
    minimumTerm?: number;
    preferredTerm?: number;
    deposit?: number;
    maintenanceResponsibility?: string;
    // Buy request fields
    maxPrice?: number;
    maxRate?: number;
    maxTsn?: number;
    maxCsn?: number;
    maxTso?: number;
    maxCso?: number;
    // Condition & documentation
    certifications?: string[];
    logbooksAvailable?: boolean;
    accidentFree?: boolean;
    flexiblePrice?: boolean;
    // Shipping
    shippingOptions?: string[];
  };
  
  // Parts-specific
  partsData?: {
    // Core identity
    itemType?: 'part' | 'tool' | 'equipment';
    partNumber?: string;
    serialNumber?: string;
    name?: string;
    manufacturer?: string;
    specificityType?: 'helicopter' | 'engine' | 'general';
    helicopterModel?: string;
    helicopterManufacturer?: string;
    helicopterVariant?: string;
    engineModel?: string;
    engineManufacturer?: string;
    engineVariant?: string;
    engineModule?: string;
    
    // Quantity & availability
    quantity?: number;
    handlingTime?: string;
    
    // Time/Cycle data (for parts)
    tsn?: number;
    csn?: number;
    tso?: number;
    cso?: number;
    lifeLimit?: number;
    tbo?: number;
    
    // Tool-specific
    calibrationStatus?: string;
    calibrationDueDate?: string;
    toolType?: string;
    
    // Equipment-specific
    equipmentCategory?: string;
    powerRequirements?: string;
    capacity?: string;
    operatingWeight?: number;
    material?: string;
    
    // Condition & certifications
    certifications?: string[];
    condition?: string;
    warranty?: string;
    description?: string;
    
    // Rental fields
    rentalRate?: number;
    rentalPeriod?: string;
    minimumTerm?: number;
    preferredTerm?: number;
    deposit?: number;
    maintenanceTerms?: string;
    
    // Buy/Request fields
    maxBudget?: number;
    maxRate?: number;
    urgency?: string;
    
    // Shipping
    shippingOptions?: string[];
    shippingMethods?: string[];
    localPickupAvailable?: boolean;
    domesticShipping?: boolean;
    internationalShipping?: boolean;
    hazmatRequired?: boolean;
    specialHandling?: string;
  };
  
  // Job-specific
  jobData?: {
    jobCategory?: string;
    employmentType?: string;
    remoteOptions?: string;
    salaryMin?: number;
    salaryMax?: number;
    salaryType?: string;
    companyName?: string;
    companyLogo?: string;
    companyDescription?: string;
    companyWebsite?: string;
    currentPosition?: string;
    experienceYearsMin?: number;
    experienceYearsMax?: number;
    flightHoursMin?: number;
    licenses?: string[];
    certifications?: string[];
    typeRatings?: string[];
    medicalClass?: string;
    benefits?: string;
    requirements?: string;
    responsibilities?: string;
    relocationOffered?: boolean;
    includesBenefits?: boolean;
    heroImage?: string;
  };
  
  // Services-specific
  servicesData?: {
    category?: string;
    listingType?: string;
    companyName?: string;
    companyLogo?: string;
    regulatoryApprovals?: string[];
    certifications?: string[];
    serviceTypes?: string[];
    // Capability IDs for resolution to actual models with icons
    helicopterCapabilityIds?: string[];
    engineCapabilityIds?: string[];
    // Fallback counts
    helicopterCapabilities?: number;
    engineCapabilities?: number;
    missionTypes?: string[];
    pricingModel?: string;
    baseRate?: number;
    rateUnit?: string;
    responseTime?: string;
    turnaroundTime?: string;
    coverageRegions?: string[];
    serviceRadius?: number;
    aogSupport?: boolean;
    aogResponseTime?: string;
    shiftCoverage?: string;
    facilitySize?: number;
    hangarCapacity?: number;
    fleetSize?: number;
    instructorCount?: number;
    simulatorTypes?: string[];
    fuelTypes?: string[];
    amenities?: string[];
    droneTypes?: string[];
    flightAuthorizations?: string[];
  };
  
  // Event handlers
  onClick?: () => void;
  onViewDetails?: () => void;
  
  // View options
  listView?: boolean;
  mobileCompact?: boolean;
  isSelected?: boolean;
  className?: string;
  
  // Management mode props
  variant?: 'default' | 'management';
  listingStatus?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  onToggleStatus?: () => void;
  onPrivacy?: () => void;
}

const MarketplaceListingCardComponent: React.FC<MarketplaceListingCardProps> = ({
  id,
  title,
  price,
  location,
  latitude,
  longitude,
  images,
  featured = false,
  listingType = 'sale',
  assetType,
  isBuyRequest = false,
  condition,
  year,
  sellerId,
  sellerName,
  helicopterData,
  engineData,
  partsData,
  jobData,
  servicesData,
  visibilitySettings,
  onClick,
  onViewDetails,
  listView = false,
  mobileCompact = false,
  isSelected = false,
  className,
  variant = 'default',
  listingStatus,
  onEdit,
  onDelete,
  onToggleStatus,
  onPrivacy,
}) => {
  // Check if listing has coordinates for map display
  const hasCoordinates = !!(latitude && longitude);
  // Use grant-aware visibility - check if price/location should be hidden
  const {
    blurPrice: shouldHidePrice,
    blurLocation: shouldHideLocation,
    blurHours: shouldHideHours,
    blurSerial: shouldHideSerial,
    blurContact: shouldHideContact,
  } = useEffectiveVisibility(id, visibilitySettings, sellerId);
  
  const { isFavorite, toggleFavorite } = useFavorites();
  const { currency } = useCurrency();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openUnifiedContact } = useUnifiedContact();
  const { toast } = useToast();

  const hasPrimaryImage = Boolean(images?.[0]);
  const primaryImage = images?.[0] || heroPlaceholder;
  const totalImages = images?.length || 0;
  const isManagement = variant === 'management';
  const isActive = listingStatus === 'active';
  const isPaused = listingStatus === 'inactive';

  // Get favorite type based on asset
  const favoriteType = assetType === 'engine' ? 'engine' : assetType === 'parts' ? 'parts' : assetType === 'jobs' ? 'jobs' : assetType === 'services' ? 'services' : 'helicopter';

  // Determine rental period for price formatting - expanded logic for all rental types
  const getRentalPeriod = (): string | undefined => {
    // For buy dry/wet lease with rate options, use the helicopterData.rentalPeriod
    if (helicopterData?.rentalPeriod) {
      return helicopterData.rentalPeriod;
    }
    // For engines - check engineData first (has rentalPeriod)
    if (assetType === 'engine' && engineData?.rentalPeriod) {
      return engineData.rentalPeriod;
    }
    // For parts - check partsData for rentalPeriod
    if (assetType === 'parts' && partsData?.rentalPeriod) {
      return partsData.rentalPeriod;
    }
    // For engine rent/rent request types, default to month
    if (listingType === 'engine_rent' || listingType === 'engine_rent_request') {
      return 'month';
    }
    // For helicopter rentals - default to month
    if (['rent', 'dry_lease', 'wet_lease', 'lease'].includes(listingType)) {
      return 'month';
    }
    // For parts rentals - default to day (tools often rented daily)
    if (listingType === 'parts_rent' || listingType === 'parts_rent_request') {
      return 'day';
    }
    return undefined;
  };

  // Get formatted price based on asset type
  const getFormattedPrice = useMemo(() => {
    // Services - use service-specific formatter
    if (assetType === 'services' && servicesData) {
      const result = formatServiceListingPrice(
        servicesData.baseRate,
        servicesData.pricingModel,
        servicesData.rateUnit,
        currency
      );
      return {
        text: result.formatted,
        displayType: result.displayType,
        showPrice: result.displayType === 'price',
      };
    }
    
    // Jobs - use salary formatter
    if (assetType === 'jobs' && jobData) {
      const salary = formatJobSalary(
        jobData.salaryMin,
        jobData.salaryMax,
        jobData.salaryType,
        'Competitive',
        currency
      );
      return {
        text: salary || 'Competitive',
        displayType: salary ? 'price' : 'none',
        showPrice: !!salary,
      };
    }
    
    // All other asset types - use standard formatter
    const formatted = formatListingPrice(
      price,
      listingType,
      currency,
      getRentalPeriod()
    );
    return {
      text: formatted,
      displayType: 'price',
      showPrice: formatted !== '—',
    };
  }, [assetType, servicesData, jobData, price, listingType, currency]);

  const formattedPrice = getFormattedPrice.text;

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (id) {
      toggleFavorite(id, favoriteType);
    }
  };

  const isListingFavorite = isFavorite(id);

  const handleMessageSeller = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Auth check - dispatch require-auth event for login modal
    if (!user) {
      window.dispatchEvent(new CustomEvent('require-auth', { 
        detail: { message: 'Please sign in to contact the seller' }
      }));
      return;
    }

    // Self-contact prevention
    if (sellerId === user.id) {
      toast({
        title: "Cannot message yourself",
        description: "This is your own listing",
        variant: "destructive"
      });
      return;
    }

    // Missing data check
    if (!id || !sellerId) {
      toast({
        title: "Error",
        description: "Unable to start conversation",
        variant: "destructive"
      });
      return;
    }

    // Open the unified contact modal (same as detail modals)
    openUnifiedContact({
      listingId: id,
      listingTitle: title,
      sellerId: sellerId,
      assetType: assetType,
    });
  };

  const isOwner = user?.id === sellerId;

  const requestField = useCallback((field: string) => {
    if (!user) {
      window.dispatchEvent(new CustomEvent('require-auth', {
        detail: { message: 'Please sign in to request this information' }
      }));
      return;
    }
    if (!id || !sellerId || sellerId === user.id) return;
    openUnifiedContact({
      listingId: id,
      listingTitle: title,
      sellerId,
      assetType,
      hiddenFields: {
        price: shouldHidePrice,
        location: shouldHideLocation,
        contact: shouldHideContact,
      },
      preselectedFields: [field],
    });
  }, [user, id, sellerId, title, assetType, shouldHidePrice, shouldHideLocation, shouldHideContact, openUnifiedContact]);

  // Asset type icon
  const AssetIcon = assetType === 'engine' ? Settings : assetType === 'parts' ? Package : Clock;

  // Render helicopter-specific specs with listing type customization
  const renderHelicopterSpecs = () => {
    if (!helicopterData) return null;
    
    const { 
      airframeHours,
      airframeLandings,
      airframeCycles, // deprecated fallback
      engineHours, 
      engineCycles,
      engineLifeRemaining,
      engineLifeLimit,
      configurationName,
      selectedEquipment,
      leaseMinPeriod,
      leaseMaxPeriod,
      includedServices,
      pilotProvided,
      urgencyLevel,
      financingStatus,
      maxAge,
      maxFlightHours
    } = helicopterData;

    // Use airframeLandings or fall back to deprecated airframeCycles
    const landingsValue = airframeLandings ?? airframeCycles;

    // Buy request layout - Requirements focused
    if (isBuyRequest) {
      const hasRequirements = maxAge || maxFlightHours;
      
      return (
        <div className="space-y-3">
          {/* Requirements Section - Always visible */}
          <div className="p-3 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/30 rounded-xl border border-amber-200/60 dark:border-amber-700/40 shadow-sm">
            <div className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400 font-bold mb-2 flex items-center gap-1.5">
              <Gauge className="w-3 h-3" />
              Aircraft Requirements
            </div>
            {hasRequirements ? (
              <div className="grid grid-cols-2 gap-3">
                {maxAge && (
                  <div className="flex items-center gap-2 p-2 bg-white/60 dark:bg-black/20 rounded-lg">
                    <Calendar className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    <div>
                      <div className="text-[10px] text-muted-foreground">Max Age</div>
                      <div className="font-bold text-sm">{maxAge} years</div>
                    </div>
                  </div>
                )}
                {maxFlightHours && (
                  <div className="flex items-center gap-2 p-2 bg-white/60 dark:bg-black/20 rounded-lg">
                    <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    <div>
                      <div className="text-[10px] text-muted-foreground">Max Hours</div>
                      <div className="font-bold text-sm">{maxFlightHours.toLocaleString()}</div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-amber-600/80 dark:text-amber-400/80 italic">
                Contact for specific requirements
              </div>
            )}
          </div>
          
          {/* Urgency & Financing badges */}
          {(urgencyLevel || financingStatus) && (
            <div className="flex flex-wrap gap-1.5">
              {urgencyLevel && (
                <Badge 
                  variant="outline" 
                  className={cn(
                    "text-[10px] capitalize font-medium",
                    urgencyLevel === 'urgent' && "border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20",
                    urgencyLevel === 'flexible' && "border-emerald-300 dark:border-emerald-700 text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20",
                    !['urgent', 'flexible'].includes(urgencyLevel) && "border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800"
                  )}
                >
                  {urgencyLevel === 'urgent' ? '🔥' : '📅'} {urgencyLevel}
                </Badge>
              )}
              {financingStatus && (
                <Badge variant="outline" className="text-[10px] bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-400 font-medium">
                  💰 {financingStatus}
                </Badge>
              )}
            </div>
          )}
          
          {/* Required Equipment */}
          {selectedEquipment && selectedEquipment.length > 0 && (
            <EquipmentBadges equipment={selectedEquipment} maxShow={5} />
          )}
          
          {/* Configuration */}
          {configurationName && (
            <ConfigurationBadge configuration={configurationName} />
          )}
        </div>
      );
    }

    // WET LEASE layout - Includes services
    if (listingType === 'wet_lease') {
      return (
        <div className="space-y-3">
          {/* Hours & Landings Grid */}
          <div className="grid grid-cols-2 gap-2">
            <LifeIndicator 
              label="Airframe"
              hours={airframeHours}
              secondary={landingsValue}
              secondaryType="landings"
            />
            <LifeIndicator 
              label="Engine"
              hours={engineHours}
              secondary={engineCycles}
              secondaryType="cycles"
              lifeRemaining={engineLifeRemaining}
              lifeLimit={engineLifeLimit}
            />
          </div>
          
          {/* Included Services - Key for wet lease */}
          <div className="p-2.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200/50 dark:border-emerald-700/30">
            <div className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400 font-semibold mb-1.5">
              Included Services
            </div>
            <div className="flex flex-wrap gap-1.5">
              {pilotProvided && (
                <Badge className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-800 dark:text-emerald-200 border-0">
                  <Users className="w-3 h-3 mr-1" />
                  Crew
                </Badge>
              )}
              {includedServices?.slice(0, 3).map((service, i) => (
                <Badge key={i} className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-800 dark:text-emerald-200 border-0">
                  <Check className="w-3 h-3 mr-1" />
                  {service}
                </Badge>
              ))}
              {(includedServices?.length || 0) > 3 && (
                <Badge variant="outline" className="text-[10px] text-emerald-600 dark:text-emerald-400">
                  +{(includedServices?.length || 0) - 3} more
                </Badge>
              )}
            </div>
          </div>
          
          {/* Lease terms */}
          {(leaseMinPeriod || leaseMaxPeriod) && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calendar className="w-3.5 h-3.5" />
              <span>
                {leaseMinPeriod && leaseMaxPeriod 
                  ? `${leaseMinPeriod}-${leaseMaxPeriod} months`
                  : leaseMinPeriod 
                    ? `Min ${leaseMinPeriod} months`
                    : `Max ${leaseMaxPeriod} months`
                }
              </span>
            </div>
          )}
          
          {/* Equipment & Config */}
          {selectedEquipment && selectedEquipment.length > 0 && (
            <EquipmentBadges equipment={selectedEquipment} maxShow={4} />
          )}
          {configurationName && (
            <ConfigurationBadge configuration={configurationName} />
          )}
        </div>
      );
    }

    // DRY LEASE layout - You provide crew
    if (listingType === 'dry_lease') {
      return (
        <div className="space-y-3">
          {/* Hours & Landings Grid */}
          <div className="grid grid-cols-2 gap-2">
            <LifeIndicator 
              label="Airframe"
              hours={airframeHours}
              secondary={landingsValue}
              secondaryType="landings"
            />
            <LifeIndicator 
              label="Engine"
              hours={engineHours}
              secondary={engineCycles}
              secondaryType="cycles"
              lifeRemaining={engineLifeRemaining}
              lifeLimit={engineLifeLimit}
            />
          </div>
          
          {/* Dry Lease Notice */}
          <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200/50 dark:border-blue-700/30">
            <div className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300">
              <HelicopterSilhouetteIcon className="w-4 h-4" />
              <span className="font-medium">Aircraft Only</span>
              <span className="text-blue-500 dark:text-blue-400">• You provide crew</span>
            </div>
          </div>
          
          {/* Lease terms */}
          {(leaseMinPeriod || leaseMaxPeriod) && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calendar className="w-3.5 h-3.5" />
              <span>
                {leaseMinPeriod && leaseMaxPeriod 
                  ? `${leaseMinPeriod}-${leaseMaxPeriod} months`
                  : leaseMinPeriod 
                    ? `Min ${leaseMinPeriod} months`
                    : `Max ${leaseMaxPeriod} months`
                }
              </span>
            </div>
          )}
          
          {/* Equipment & Config */}
          {selectedEquipment && selectedEquipment.length > 0 && (
            <EquipmentBadges equipment={selectedEquipment} maxShow={4} />
          )}
          {configurationName && (
            <ConfigurationBadge configuration={configurationName} />
          )}
        </div>
      );
    }

    // FOR SALE layout (default) - Standard operational data
    return (
      <div className="space-y-3">
        {/* Hours & Landings Grid */}
        <div className="grid grid-cols-2 gap-2">
          <LifeIndicator 
            label="Airframe"
            hours={airframeHours}
            secondary={landingsValue}
            secondaryType="landings"
          />
          <LifeIndicator 
            label="Engine"
            hours={engineHours}
            secondary={engineCycles}
            secondaryType="cycles"
            lifeRemaining={engineLifeRemaining}
            lifeLimit={engineLifeLimit}
          />
        </div>
        
        {/* Equipment badges - 2 row layout */}
        {selectedEquipment && selectedEquipment.length > 0 && (
          <EquipmentBadges equipment={selectedEquipment} maxShow={6} />
        )}
        
        {/* Configuration badge - Always after equipment */}
        {configurationName && (
          <ConfigurationBadge configuration={configurationName} />
        )}
      </div>
    );
  };

  // Render engine specs with listing-type specific layouts
  const renderEngineSpecs = () => {
    if (!engineData) return null;
    
    const {
      tsn, csn, tso, cso, tbo, powerOutput, powerUnit,
      rentalRate, rentalPeriod, minimumTerm, deposit, maintenanceResponsibility,
      maxPrice, maxRate, maxTsn, maxCsn, maxTso, maxCso, preferredTerm,
      certifications, logbooksAvailable, accidentFree, flexiblePrice
    } = engineData;

    // Helper for certification badges - consistent styling
    const renderCertifications = () => {
      if (!certifications || certifications.length === 0) return null;
      return (
        <div className="pt-2.5 mt-2.5 border-t border-border/40">
          <div className="flex items-center gap-1.5 flex-wrap">
            {certifications.slice(0, 4).map((cert, i) => (
              <Badge 
                key={i} 
                variant="outline" 
                className="text-[10px] px-2 py-0.5 bg-blue-50/80 dark:bg-blue-900/30 border-blue-200/60 dark:border-blue-700/40 text-blue-700 dark:text-blue-300 font-medium"
              >
                {cert}
              </Badge>
            ))}
            {certifications.length > 4 && (
              <Badge variant="outline" className="text-[10px] px-2 py-0.5 text-muted-foreground">
                +{certifications.length - 4}
              </Badge>
            )}
          </div>
        </div>
      );
    };

    // Helper for logbooks badge - consistent styling
    const renderLogbooksBadge = () => {
      if (!logbooksAvailable) return null;
      return (
        <Badge 
          variant="outline" 
          className="text-[10px] px-2 py-0.5 flex items-center gap-1 bg-emerald-50/80 dark:bg-emerald-900/20 border-emerald-200/60 dark:border-emerald-700/40 text-emerald-700 dark:text-emerald-400 font-medium"
        >
          <FileCheck className="w-3 h-3" />
          Logbooks Available
        </Badge>
      );
    };

    // ENGINE BUY REQUEST layout - Budget & requirements focused
    if (listingType === 'engine_buy' || isBuyRequest) {
      const hasRequirements = maxTsn || maxCsn || maxTso || condition;
      const requirementCount = [maxTsn, maxCsn, maxTso, condition].filter(Boolean).length;
      const hasEngineIdentity = engineData?.manufacturer || engineData?.model || engineData?.variant;
      const hasDocRequirements = logbooksAvailable || accidentFree;
      
      return (
        <div className="space-y-3 relative z-10">
          {/* Engine Identity - Manufacturer/Model/Variant */}
          {hasEngineIdentity && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {engineData?.manufacturer && (
                <Badge variant="secondary" className="text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                  {engineData.manufacturer}
                </Badge>
              )}
              {engineData?.model && (
                <Badge variant="outline" className="text-[10px] font-medium">
                  {engineData.model}
                </Badge>
              )}
              {engineData?.variant && (
                <Badge variant="outline" className="text-[10px] bg-slate-50 dark:bg-slate-800/50">
                  {engineData.variant}
                </Badge>
              )}
            </div>
          )}

          {/* Budget Section */}
          <div className="p-3 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/30 rounded-xl border border-amber-200/60 dark:border-amber-700/40 shadow-sm">
            <div className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400 font-bold mb-2 flex items-center gap-1.5">
              <Gauge className="w-3 h-3" />
              Budget
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-amber-800 dark:text-amber-300">
                Up to ${(maxPrice || price)?.toLocaleString()}
              </span>
              {flexiblePrice && (
                <Badge className="text-[9px] bg-amber-100 text-amber-700 dark:bg-amber-800/40 dark:text-amber-300 border-0">
                  Flexible
                </Badge>
              )}
            </div>
          </div>
          
          {/* Requirements Section */}
          {hasRequirements && (
            <div className="p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200/50 dark:border-slate-700/30">
              <div className="text-[10px] uppercase tracking-wide text-slate-600 dark:text-slate-400 font-semibold mb-2 flex items-center gap-1.5">
                <Settings className="w-3 h-3" />
                Maximum Requirements
              </div>
              {/* Single condition - full width centered */}
              {requirementCount === 1 && condition && !maxTsn && !maxCsn && !maxTso ? (
                <div className="flex items-center justify-center gap-2 p-2.5 bg-white/60 dark:bg-black/20 rounded-lg">
                  <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  <span className="font-semibold text-sm capitalize">{condition}</span>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {maxTsn && (
                    <div className="flex items-center gap-2 p-2 bg-white/60 dark:bg-black/20 rounded-lg">
                      <Clock className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-[9px] text-muted-foreground leading-tight">Max TSN</div>
                        <div className="font-bold text-xs tabular-nums">{maxTsn.toLocaleString()} hrs</div>
                      </div>
                    </div>
                  )}
                  {maxCsn && (
                    <div className="flex items-center gap-2 p-2 bg-white/60 dark:bg-black/20 rounded-lg">
                      <Hash className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-[9px] text-muted-foreground leading-tight">Max CSN</div>
                        <div className="font-bold text-xs tabular-nums">{maxCsn.toLocaleString()}</div>
                      </div>
                    </div>
                  )}
                  {maxTso && (
                    <div className="flex items-center gap-2 p-2 bg-white/60 dark:bg-black/20 rounded-lg">
                      <Settings className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-[9px] text-muted-foreground leading-tight">Max TSO</div>
                        <div className="font-bold text-xs tabular-nums">{maxTso.toLocaleString()} hrs</div>
                      </div>
                    </div>
                  )}
                  {condition && (maxTsn || maxCsn || maxTso) && (
                    <div className="flex items-center gap-2 p-2 bg-white/60 dark:bg-black/20 rounded-lg">
                      <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-[9px] text-muted-foreground leading-tight">Condition</div>
                        <div className="font-bold text-xs capitalize">{condition}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Documentation Requirements */}
          {hasDocRequirements && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {logbooksAvailable && (
                <Badge variant="outline" className="text-[10px] px-2 py-0.5 flex items-center gap-1 bg-emerald-50/80 dark:bg-emerald-900/20 border-emerald-200/60 dark:border-emerald-700/40 text-emerald-700 dark:text-emerald-400 font-medium">
                  <FileCheck className="w-3 h-3" />
                  Logbooks Required
                </Badge>
              )}
              {accidentFree && (
                <Badge variant="outline" className="text-[10px] px-2 py-0.5 flex items-center gap-1 bg-emerald-50/80 dark:bg-emerald-900/20 border-emerald-200/60 dark:border-emerald-700/40 text-emerald-700 dark:text-emerald-400 font-medium">
                  <Check className="w-3 h-3" />
                  Accident-Free Only
                </Badge>
              )}
            </div>
          )}
          
          {/* Certifications with separator */}
          {renderCertifications()}
        </div>
      );
    }

    // ENGINE RENT REQUEST layout - Budget & terms focused
    if (listingType === 'engine_rent_request') {
      const hasRequirements = maxTsn || maxTso || condition;
      const hasEngineIdentity = engineData?.manufacturer || engineData?.model || engineData?.variant;
      const hasDocRequirements = logbooksAvailable || accidentFree;
      
      return (
        <div className="space-y-3 relative z-10">
          {/* Engine Identity - Manufacturer/Model/Variant */}
          {hasEngineIdentity && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {engineData?.manufacturer && (
                <Badge variant="secondary" className="text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                  {engineData.manufacturer}
                </Badge>
              )}
              {engineData?.model && (
                <Badge variant="outline" className="text-[10px] font-medium">
                  {engineData.model}
                </Badge>
              )}
              {engineData?.variant && (
                <Badge variant="outline" className="text-[10px] bg-slate-50 dark:bg-slate-800/50">
                  {engineData.variant}
                </Badge>
              )}
            </div>
          )}

          {/* Budget Section */}
          <div className="p-3 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/40 dark:to-amber-950/30 rounded-xl border border-orange-200/60 dark:border-orange-700/40 shadow-sm">
            <div className="text-[10px] uppercase tracking-wider text-orange-700 dark:text-orange-400 font-bold mb-2 flex items-center gap-1.5">
              <Gauge className="w-3 h-3" />
              Rental Budget
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg font-bold text-orange-800 dark:text-orange-300">
                Max ${(maxRate || price)?.toLocaleString()}/{rentalPeriod || 'month'}
              </span>
              {preferredTerm && (
                <Badge className="text-[9px] bg-orange-100 text-orange-700 dark:bg-orange-800/40 dark:text-orange-300 border-0">
                  {preferredTerm} mo term
                </Badge>
              )}
            </div>
            {deposit && (
              <div className="text-xs text-orange-600 dark:text-orange-400 mt-1.5 flex items-center gap-1">
                <Check className="w-3 h-3" />
                Deposit: ${deposit.toLocaleString()}
              </div>
            )}
          </div>
          
          {/* Requirements */}
          {hasRequirements && (
            <div className="p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200/50 dark:border-slate-700/30">
              <div className="text-[10px] uppercase tracking-wide text-slate-600 dark:text-slate-400 font-semibold mb-2 flex items-center gap-1.5">
                <Settings className="w-3 h-3" />
                Requirements
              </div>
              <div className="grid grid-cols-2 gap-2">
                {maxTsn && (
                  <div className="flex items-center gap-2 p-2 bg-white/60 dark:bg-black/20 rounded-lg">
                    <Clock className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[9px] text-muted-foreground leading-tight">Max TSN</div>
                      <div className="font-bold text-xs tabular-nums">{maxTsn.toLocaleString()}</div>
                    </div>
                  </div>
                )}
                {maxTso && (
                  <div className="flex items-center gap-2 p-2 bg-white/60 dark:bg-black/20 rounded-lg">
                    <Settings className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[9px] text-muted-foreground leading-tight">Max TSO</div>
                      <div className="font-bold text-xs tabular-nums">{maxTso.toLocaleString()}</div>
                    </div>
                  </div>
                )}
                {condition && (
                  <div className={cn(
                    "flex items-center gap-2 p-2 bg-white/60 dark:bg-black/20 rounded-lg",
                    !maxTsn && !maxTso && "col-span-2 justify-center"
                  )}>
                    <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[9px] text-muted-foreground leading-tight">Condition</div>
                      <div className="font-bold text-xs capitalize">{condition}+</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Documentation Requirements */}
          {hasDocRequirements && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {logbooksAvailable && (
                <Badge variant="outline" className="text-[10px] px-2 py-0.5 flex items-center gap-1 bg-emerald-50/80 dark:bg-emerald-900/20 border-emerald-200/60 dark:border-emerald-700/40 text-emerald-700 dark:text-emerald-400 font-medium">
                  <FileCheck className="w-3 h-3" />
                  Logbooks Required
                </Badge>
              )}
              {accidentFree && (
                <Badge variant="outline" className="text-[10px] px-2 py-0.5 flex items-center gap-1 bg-emerald-50/80 dark:bg-emerald-900/20 border-emerald-200/60 dark:border-emerald-700/40 text-emerald-700 dark:text-emerald-400 font-medium">
                  <Check className="w-3 h-3" />
                  Accident-Free Only
                </Badge>
              )}
            </div>
          )}
          
          {/* Certifications with separator */}
          {renderCertifications()}
        </div>
      );
    }

    // ENGINE RENT layout - Rental terms focused
    if (listingType === 'engine_rent') {
      const hasEngineIdentity = engineData?.manufacturer || engineData?.model || engineData?.variant;
      
      return (
        <div className="space-y-3 relative z-10">
          {/* Engine Identity Header */}
          {hasEngineIdentity && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {engineData?.manufacturer && (
                <Badge variant="secondary" className="text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                  {engineData.manufacturer}
                </Badge>
              )}
              {engineData?.model && (
                <Badge variant="outline" className="text-[10px] font-medium">
                  {engineData.model}
                </Badge>
              )}
              {engineData?.variant && (
                <Badge variant="outline" className="text-[10px] bg-slate-50 dark:bg-slate-800/50">
                  {engineData.variant}
                </Badge>
              )}
            </div>
          )}

          {/* TSN/TSO Grid */}
          <div className="grid grid-cols-2 gap-2">
            <LifeIndicator 
              label="TSN"
              hours={tsn}
              secondary={csn}
              secondaryType="cycles"
            />
            {(tso || cso) ? (
              <LifeIndicator 
                label="Since OH"
                hours={tso}
                secondary={cso}
                secondaryType="cycles"
                lifeRemaining={tbo ? (tbo - (tso || 0)) : undefined}
                lifeLimit={tbo}
              />
            ) : (
              <div className="p-3 rounded-xl border border-dashed border-slate-200/60 dark:border-slate-700/40 bg-slate-50/50 dark:bg-slate-800/30 flex items-center justify-center">
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">No OH Data</span>
              </div>
            )}
          </div>
          
          {/* Rental Terms */}
          <div className="p-2.5 bg-blue-50/80 dark:bg-blue-900/20 rounded-lg border border-blue-200/50 dark:border-blue-700/30">
            <div className="text-[10px] uppercase tracking-wide text-blue-700 dark:text-blue-400 font-semibold mb-2 flex items-center gap-1.5">
              <Calendar className="w-3 h-3" />
              Rental Terms
            </div>
            <div className="grid grid-cols-3 gap-2">
              {rentalRate && (
                <div className="text-center p-1.5 bg-white/60 dark:bg-black/20 rounded">
                  <div className="font-bold text-sm text-blue-800 dark:text-blue-300">${rentalRate.toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground">/{rentalPeriod || 'month'}</div>
                </div>
              )}
              {minimumTerm && (
                <div className="text-center p-1.5 bg-white/60 dark:bg-black/20 rounded">
                  <div className="font-bold text-sm text-blue-800 dark:text-blue-300">{minimumTerm}</div>
                  <div className="text-[9px] text-muted-foreground">min months</div>
                </div>
              )}
              {deposit && (
                <div className="text-center p-1.5 bg-white/60 dark:bg-black/20 rounded">
                  <div className="font-bold text-sm text-blue-800 dark:text-blue-300">${(deposit/1000).toFixed(0)}k</div>
                  <div className="text-[9px] text-muted-foreground">deposit</div>
                </div>
              )}
            </div>
            {maintenanceResponsibility && (
              <div className="flex items-center justify-center gap-1.5 mt-2 pt-2 border-t border-blue-200/40 dark:border-blue-700/30 text-xs text-blue-700 dark:text-blue-300">
                <Wrench className="w-3 h-3" />
                <span className="capitalize">{maintenanceResponsibility} maintains</span>
              </div>
            )}
          </div>
          
          {/* Power, Condition & Extras Row */}
          <div className="flex items-center gap-2 flex-wrap">
            {powerOutput && (
              <Badge variant="secondary" className="text-[10px] gap-1 bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
                <Zap className="w-3 h-3" />
                {powerOutput} {powerUnit || 'SHP'}
              </Badge>
            )}
            {condition && (
              <Badge variant="outline" className="text-[10px] capitalize font-medium">
                {condition}
              </Badge>
            )}
            {renderLogbooksBadge()}
          </div>
          
          {/* Certifications with separator */}
          {renderCertifications()}
        </div>
      );
    }

    // ENGINE SELL layout (default) - Full specs display
    const hasEngineIdentity = engineData?.manufacturer || engineData?.model || engineData?.variant;
    
    return (
      <div className="space-y-3 relative z-10">
        {/* Engine Identity Header */}
        {hasEngineIdentity && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {engineData?.manufacturer && (
              <Badge variant="secondary" className="text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                {engineData.manufacturer}
              </Badge>
            )}
            {engineData?.model && (
              <Badge variant="outline" className="text-[10px] font-medium">
                {engineData.model}
              </Badge>
            )}
            {engineData?.variant && (
              <Badge variant="outline" className="text-[10px] bg-slate-50 dark:bg-slate-800/50">
                {engineData.variant}
              </Badge>
            )}
          </div>
        )}

        {/* TSN/TSO Grid */}
        <div className="grid grid-cols-2 gap-2">
          <LifeIndicator 
            label="TSN"
            hours={tsn}
            secondary={csn}
            secondaryType="cycles"
          />
          {(tso || cso) ? (
            <LifeIndicator 
              label="Since OH"
              hours={tso}
              secondary={cso}
              secondaryType="cycles"
              lifeRemaining={tbo ? (tbo - (tso || 0)) : undefined}
              lifeLimit={tbo}
            />
          ) : (
            <div className="p-3 rounded-xl border border-dashed border-slate-200/60 dark:border-slate-700/40 bg-slate-50/50 dark:bg-slate-800/30 flex items-center justify-center">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">No OH Data</span>
            </div>
          )}
        </div>
        
        {/* Power, Condition & Extras Row - Consistent alignment */}
        <div className="flex items-center gap-2 flex-wrap">
          {powerOutput && (
            <Badge variant="secondary" className="text-[10px] gap-1 bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
              <Zap className="w-3 h-3" />
              {powerOutput} {powerUnit || 'SHP'}
            </Badge>
          )}
          {condition && (
            <Badge variant="outline" className="text-[10px] capitalize font-medium">
              {condition}
            </Badge>
          )}
          {accidentFree && (
            <Badge variant="outline" className="text-[10px] flex items-center gap-1 bg-emerald-50/80 dark:bg-emerald-900/20 border-emerald-200/60 dark:border-emerald-700/40 text-emerald-700 dark:text-emerald-400">
              <Check className="w-3 h-3" />
              Accident Free
            </Badge>
          )}
          {renderLogbooksBadge()}
        </div>
        
        {/* Certifications with separator */}
        {renderCertifications()}
      </div>
    );
  };

  // Render parts specs - listing-type-specific layouts for stunning displays
  const renderPartsSpecs = () => {
    if (!partsData) return null;
    
    const {
      itemType,
      partNumber,
      serialNumber,
      name,
      manufacturer,
      specificityType,
      helicopterModel,
      helicopterManufacturer,
      helicopterVariant,
      engineModel,
      engineManufacturer,
      engineVariant,
      engineModule,
      quantity,
      handlingTime,
      tsn,
      csn,
      tso,
      cso,
      lifeLimit,
      tbo,
      calibrationStatus,
      calibrationDueDate,
      toolType,
      equipmentCategory,
      powerRequirements,
      capacity,
      certifications = [],
      condition: partsCondition,
      warranty,
      description,
      rentalRate,
      rentalPeriod,
      minimumTerm,
      preferredTerm,
      deposit,
      maxBudget,
      maxRate,
      urgency,
      shippingOptions = [],
      localPickupAvailable,
      domesticShipping,
      internationalShipping,
      hazmatRequired,
      specialHandling,
    } = partsData;
    
    // Helper: render item type icon
    const ItemTypeIcon = () => {
      if (itemType === 'tool') return <Wrench className="w-4 h-4" />;
      if (itemType === 'equipment') return <Settings className="w-4 h-4" />;
      return <Package className="w-4 h-4" />;
    };
    
    // Helper: get compatibility label
    const getCompatibilityLabel = () => {
      if (specificityType === 'helicopter') {
        const parts = [helicopterManufacturer, helicopterModel, helicopterVariant].filter(Boolean);
        return parts.length > 0 ? parts.join(' ') : 'Helicopter Specific';
      }
      if (specificityType === 'engine') {
        const parts = [engineManufacturer, engineModel, engineVariant].filter(Boolean);
        if (engineModule) parts.push(`(${engineModule})`);
        return parts.length > 0 ? parts.join(' ') : 'Engine Specific';
      }
      return 'Universal';
    };
    
    // Helper: render part/serial number row - ALWAYS show if available
    const PartSerialRow = () => {
      if (!partNumber && !serialNumber) return null;
      return (
        <div className="flex items-center gap-1.5 flex-wrap">
          {partNumber && (
            <Badge variant="outline" className="text-[10px] font-mono bg-slate-50 dark:bg-slate-800 px-2 h-5">
              <Hash className="w-3 h-3 mr-0.5" />
              {partNumber}
            </Badge>
          )}
          {serialNumber && (
            <Badge variant="outline" className="text-[10px] font-mono bg-slate-50 dark:bg-slate-800 px-2 h-5">
              S/N: {shouldHideSerial ? '••••••' : serialNumber}
            </Badge>
          )}
        </div>
      );
    };
    
    // Helper: render certifications
    const CertificationBadges = () => {
      if (!certifications || certifications.length === 0) return null;
      return (
        <div className="flex flex-wrap gap-1">
          {certifications.slice(0, 3).map((cert, i) => (
            <Badge key={i} className="text-[9px] h-5 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200/60 dark:border-emerald-700/40">
              <FileCheck className="w-2.5 h-2.5 mr-1" />
              {cert}
            </Badge>
          ))}
          {certifications.length > 3 && (
            <Badge variant="outline" className="text-[9px] h-5">+{certifications.length - 3}</Badge>
          )}
        </div>
      );
    };
    
    // Helper: render shipping badges
    const ShippingBadges = () => {
      const hasShipping = localPickupAvailable || domesticShipping || internationalShipping || shippingOptions?.length > 0;
      if (!hasShipping) return null;
      return (
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
          <Truck className="w-3 h-3 flex-shrink-0" />
          {localPickupAvailable && <span className="flex items-center gap-0.5"><Check className="w-3 h-3 text-emerald-500" /> Local</span>}
          {domesticShipping && <span className="flex items-center gap-0.5"><Check className="w-3 h-3 text-emerald-500" /> Domestic</span>}
          {internationalShipping && <span className="flex items-center gap-0.5"><Check className="w-3 h-3 text-emerald-500" /> Intl</span>}
        </div>
      );
    };

    // Unified Compatibility Box Component - consistent display across all listing types
    const CompatibilityBox = ({ variant }: { variant: 'sell' | 'rent' | 'buy' | 'rent_request' }) => {
      const compatibilityLabel = getCompatibilityLabel();
      
      const colorClasses = {
        sell: 'bg-slate-50 border-slate-200 dark:bg-slate-800/50 dark:border-slate-700/30',
        rent: 'bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200/60 dark:from-blue-950/40 dark:to-cyan-950/30 dark:border-blue-700/40',
        buy: 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200/50 dark:from-amber-950/30 dark:to-orange-950/30 dark:border-amber-700/30',
        rent_request: 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-200/50 dark:from-orange-950/30 dark:to-red-950/30 dark:border-orange-700/30',
      };
      
      const iconColorClasses = {
        sell: 'text-slate-600 dark:text-slate-400',
        rent: 'text-blue-600 dark:text-blue-400',
        buy: 'text-amber-600 dark:text-amber-400',
        rent_request: 'text-orange-600 dark:text-orange-400',
      };

      const textColorClasses = {
        sell: 'text-slate-900 dark:text-slate-200',
        rent: 'text-blue-900 dark:text-blue-200',
        buy: 'text-amber-900 dark:text-amber-200',
        rent_request: 'text-orange-900 dark:text-orange-200',
      };

      const labelClasses = {
        sell: 'text-slate-500 dark:text-slate-400',
        rent: 'text-blue-600/70 dark:text-blue-400/70',
        buy: 'text-amber-700/70 dark:text-amber-400/70',
        rent_request: 'text-orange-700/70 dark:text-orange-400/70',
      };

      const headerLabels = {
        sell: 'Fits',
        rent: 'Fits',
        buy: 'Looking For',
        rent_request: 'Need to Rent',
      };

      return (
        <div className={cn("p-2.5 rounded-lg border", colorClasses[variant])}>
          {/* Header Label for Requests */}
          {(variant === 'buy' || variant === 'rent_request') && (
            <div className={cn("text-[10px] uppercase tracking-wide font-semibold mb-1.5 flex items-center gap-1", labelClasses[variant])}>
              {specificityType === 'helicopter' && <HelicopterSilhouetteIcon className="w-3 h-3" />}
              {specificityType === 'engine' && <Zap className="w-3 h-3" />}
              {specificityType === 'general' && <Globe className="w-3 h-3" />}
              {headerLabels[variant]}
            </div>
          )}
          
          {/* Main Compatibility Label */}
          <div className="flex items-center gap-1.5">
            {(variant === 'sell' || variant === 'rent') && (
              <>
                {specificityType === 'helicopter' && <HelicopterSilhouetteIcon className={cn("w-4 h-4")} />}
                {specificityType === 'engine' && <Zap className={cn("w-4 h-4", iconColorClasses[variant])} />}
                {specificityType === 'general' && <Globe className={cn("w-4 h-4", iconColorClasses[variant])} />}
              </>
            )}
            <span className={cn("text-sm font-semibold", textColorClasses[variant])}>
              {compatibilityLabel}
            </span>
          </div>
          
          {/* Variant and Module details - always shown if available */}
          {(helicopterVariant || engineVariant || engineModule) && (
            <div className={cn("mt-1.5 flex items-center gap-3 text-[10px]", labelClasses[variant])}>
              {(helicopterVariant || engineVariant) && (
                <span className="flex items-center gap-1">
                  <span className="opacity-70">Variant:</span>
                  <span className={cn("font-semibold", textColorClasses[variant])}>
                    {helicopterVariant || engineVariant}
                  </span>
                </span>
              )}
              {engineModule && (
                <span className="flex items-center gap-1">
                  <span className="opacity-70">Module:</span>
                  <span className={cn("font-semibold", textColorClasses[variant])}>
                    {engineModule}
                  </span>
                </span>
              )}
            </div>
          )}
          
          {/* Part number for requests */}
          {(variant === 'buy' || variant === 'rent_request') && partNumber && (
            <div className={cn("text-xs mt-1.5 font-mono", labelClasses[variant])}>
              P/N: {partNumber}
            </div>
          )}
        </div>
      );
    };

    // =============================================
    // PARTS SELL (parts_sell_aircraft, parts_sell_engine, parts_sell_general)
    // =============================================
    if (listingType?.startsWith('parts_sell')) {
      return (
        <div className="space-y-2.5 relative z-10">
          {/* Header Row: Item Type + Manufacturer + Model + Quantity */}
          <div className="flex items-center gap-1.5 flex-wrap justify-between">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-0 shadow-sm text-[10px] h-6 px-2">
                <ItemTypeIcon />
                <span className="ml-1 capitalize">{itemType || 'Part'}</span>
              </Badge>
              {/* Manufacturer Badge */}
              {(helicopterManufacturer || engineManufacturer) && (
                <Badge variant="outline" className="text-[10px] h-5 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 font-semibold">
                  {helicopterManufacturer || engineManufacturer}
                </Badge>
              )}
              {/* Model Badge */}
              {(helicopterModel || engineModel) && (
                <Badge variant="outline" className="text-[10px] h-5 bg-white dark:bg-slate-800 font-medium">
                  {helicopterModel || engineModel}
                </Badge>
              )}
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 border-0 text-[10px] h-5">
              Qty: {quantity || 1}
            </Badge>
          </div>
          
          {/* Unified Compatibility Box */}
          <CompatibilityBox variant="sell" />
          
          {/* Part Number & Serial Number - ALWAYS show if available */}
          <PartSerialRow />
          
          {/* Condition + Warranty Row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {partsCondition && (
              <Badge className={cn(
                "text-[10px] h-5 capitalize font-medium border-0",
                partsCondition === 'new' && "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400",
                partsCondition === 'serviceable' && "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400",
                partsCondition === 'overhauled' && "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400",
                partsCondition === 'as_removed' && "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400",
                !['new', 'serviceable', 'overhauled', 'as_removed'].includes(partsCondition) && "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400"
              )}>
                {partsCondition.replace(/_/g, ' ')}
              </Badge>
            )}
            {warranty && (
              <Badge className="text-[9px] h-5 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200/60 dark:border-emerald-700/40">
                <Shield className="w-2.5 h-2.5 mr-1" />
                {warranty}
              </Badge>
            )}
          </div>
          
          {/* Time/Cycle Grid for Parts */}
          {(tsn || csn || tso || cso) && (
            <div className="grid grid-cols-2 gap-2">
              {(tsn || csn) && (
                <LifeIndicator 
                  label="Total Time"
                  hours={tsn}
                  secondary={csn}
                  secondaryType="cycles"
                />
              )}
              {(tso || cso) && (
                <LifeIndicator 
                  label="Since OH"
                  hours={tso}
                  secondary={cso}
                  secondaryType="cycles"
                  lifeRemaining={tbo ? (tbo - (tso || 0)) : undefined}
                  lifeLimit={tbo}
                />
              )}
            </div>
          )}
          
          {/* Tool-specific: Calibration */}
          {itemType === 'tool' && calibrationStatus && (
            <div className={cn(
              "p-2 rounded-lg border flex items-center gap-2",
              calibrationStatus === 'current' && "bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-700/40",
              calibrationStatus === 'due_soon' && "bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700/40",
              calibrationStatus === 'expired' && "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-700/40"
            )}>
              <Gauge className="w-4 h-4" />
              <div>
                <div className="font-semibold text-xs capitalize">
                  {calibrationStatus.replace(/_/g, ' ')}
                  {calibrationDueDate && <span className="text-muted-foreground font-normal ml-1">(Due: {new Date(calibrationDueDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })})</span>}
                </div>
              </div>
            </div>
          )}
          
          {/* Equipment-specific: Power Requirements */}
          {itemType === 'equipment' && (powerRequirements || capacity) && (
            <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
              {powerRequirements && (
                <Badge variant="outline" className="text-[10px] h-5">
                  <Zap className="w-2.5 h-2.5 mr-0.5" />
                  {powerRequirements}
                </Badge>
              )}
              {capacity && (
                <Badge variant="outline" className="text-[10px] h-5">
                  {capacity}
                </Badge>
              )}
            </div>
          )}
          
          {/* Certifications */}
          <CertificationBadges />
          
          {/* Shipping Options */}
          <ShippingBadges />
          
          {/* Hazmat/Special handling warnings */}
          {(hazmatRequired || specialHandling) && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {hazmatRequired && (
                <Badge variant="outline" className="text-[9px] h-5 border-orange-300 dark:border-orange-700 text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20">
                  ⚠️ Hazmat
                </Badge>
              )}
              {specialHandling && (
                <Badge variant="outline" className="text-[9px] h-5 border-slate-300 dark:border-slate-600">
                  📦 {specialHandling}
                </Badge>
              )}
            </div>
          )}
        </div>
      );
    }

    // =============================================
    // PARTS RENT (parts_rent)
    // =============================================
    if (listingType === 'parts_rent') {
      return (
        <div className="space-y-2.5 relative z-10">
          {/* Header Row: Item Type + Manufacturer + Model + Tool Type */}
          <div className="flex items-center gap-1.5 flex-wrap justify-between">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white border-0 shadow-sm text-[10px] h-6 px-2">
                <ItemTypeIcon />
                <span className="ml-1 capitalize">{itemType || 'Part'}</span>
              </Badge>
              {/* Manufacturer Badge */}
              {(helicopterManufacturer || engineManufacturer) && (
                <Badge variant="outline" className="text-[10px] h-5 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 font-semibold">
                  {helicopterManufacturer || engineManufacturer}
                </Badge>
              )}
              {/* Model Badge */}
              {(helicopterModel || engineModel) && (
                <Badge variant="outline" className="text-[10px] h-5 bg-white dark:bg-slate-800 font-medium">
                  {helicopterModel || engineModel}
                </Badge>
              )}
            </div>
            {toolType && (
              <Badge variant="outline" className="text-[10px] h-5 bg-background">
                {toolType}
              </Badge>
            )}
          </div>
          
          {/* Unified Compatibility Box */}
          <CompatibilityBox variant="rent" />
          
          {/* Part Number & Serial Number */}
          <PartSerialRow />
          
          {/* Rental Terms Section - Compact */}
          <div className="p-2.5 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/40 dark:to-cyan-950/30 rounded-xl border border-blue-200/60 dark:border-blue-700/40 shadow-sm">
            <div className="text-[10px] uppercase tracking-wider text-blue-700 dark:text-blue-400 font-bold mb-2 flex items-center gap-1.5">
              <Calendar className="w-3 h-3" />
              Rental Terms
            </div>
            <div className="grid grid-cols-2 gap-2">
              {rentalRate && (
                <div className="text-center p-1.5 bg-white/60 dark:bg-black/20 rounded-lg">
                  <div className="font-bold text-base text-blue-800 dark:text-blue-300">${rentalRate.toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground">/{rentalPeriod || 'day'}</div>
                </div>
              )}
              {minimumTerm && (
                <div className="text-center p-1.5 bg-white/60 dark:bg-black/20 rounded-lg">
                  <div className="font-bold text-base text-blue-800 dark:text-blue-300">{minimumTerm}</div>
                  <div className="text-[9px] text-muted-foreground">min {rentalPeriod || 'day'}s</div>
                </div>
              )}
            </div>
            {deposit && (
              <div className="text-xs text-blue-600 dark:text-blue-400 mt-1.5 flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                Deposit: ${deposit.toLocaleString()}
              </div>
            )}
          </div>
          
          {/* Tool Calibration Status */}
          {itemType === 'tool' && calibrationStatus && (
            <div className={cn(
              "p-2 rounded-lg border flex items-center gap-2",
              calibrationStatus === 'current' && "bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-700/40",
              calibrationStatus === 'due_soon' && "bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700/40",
              calibrationStatus === 'expired' && "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-700/40"
            )}>
              <Gauge className="w-4 h-4" />
              <div>
                <div className="text-[10px] text-muted-foreground">Calibration</div>
                <div className="font-semibold text-xs capitalize">
                  {calibrationStatus.replace(/_/g, ' ')}
                  {calibrationDueDate && <span className="text-muted-foreground font-normal ml-1">(Due: {new Date(calibrationDueDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })})</span>}
                </div>
              </div>
            </div>
          )}
          
          {/* Condition & Certifications Row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {partsCondition && (
              <Badge variant="outline" className="text-[10px] h-5 capitalize bg-background">
                {partsCondition.replace(/_/g, ' ')}
              </Badge>
            )}
            {certifications && certifications.slice(0, 2).map((cert, i) => (
              <Badge key={i} className="text-[9px] h-5 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200/60 dark:border-emerald-700/40">
                {cert}
              </Badge>
            ))}
          </div>
        </div>
      );
    }

    // =============================================
    // PARTS BUY REQUEST (parts_buy_aircraft, parts_buy_engine, parts_buy_general)
    // =============================================
    if (listingType?.startsWith('parts_buy')) {
      return (
        <div className="space-y-2.5 relative z-10">
          {/* Header: WANTED + Item Type + Manufacturer + Model */}
          <div className="flex items-center gap-1.5 flex-wrap justify-between">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-sm text-[10px] h-6 px-2 font-bold">
                🔍 WANTED
              </Badge>
              <Badge variant="outline" className="text-[10px] h-5 capitalize bg-background">
                <ItemTypeIcon />
                <span className="ml-1">{itemType || 'Part'}</span>
              </Badge>
              {/* Manufacturer Badge */}
              {(helicopterManufacturer || engineManufacturer) && (
                <Badge variant="outline" className="text-[10px] h-5 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300 font-semibold">
                  {helicopterManufacturer || engineManufacturer}
                </Badge>
              )}
              {/* Model Badge */}
              {(helicopterModel || engineModel) && (
                <Badge variant="outline" className="text-[10px] h-5 bg-white dark:bg-slate-800 font-medium">
                  {helicopterModel || engineModel}
                </Badge>
              )}
            </div>
          </div>
          
          {/* Unified Compatibility Box */}
          <CompatibilityBox variant="buy" />
          
          {/* Quantity & Condition Row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 border-0 text-[10px] h-5">
              Qty: {quantity || 1}
            </Badge>
            {partsCondition && (
              <Badge variant="outline" className="text-[10px] h-5 capitalize bg-background">
                <Check className="w-2.5 h-2.5 mr-0.5" />
                {partsCondition}+ OK
              </Badge>
            )}
          </div>
          
          {/* Urgency Badge - Prominent */}
          {urgency && (
            <Badge className={cn(
              "text-[10px] h-6 border-0 font-medium",
              urgency === 'immediate' && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
              urgency === 'within_week' && "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
              urgency === 'within_month' && "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
              !['immediate', 'within_week', 'within_month'].includes(urgency) && "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400"
            )}>
              {urgency === 'immediate' && '🔥 Immediate Need'}
              {urgency === 'within_week' && '📅 Within 1 Week'}
              {urgency === 'within_month' && '📆 Within 1 Month'}
              {!['immediate', 'within_week', 'within_month'].includes(urgency) && urgency.replace(/_/g, ' ')}
            </Badge>
          )}
          
          {/* Description preview if available */}
          {description && (
            <div className="text-xs text-muted-foreground line-clamp-2 italic">
              "{description.slice(0, 100)}{description.length > 100 ? '...' : ''}"
            </div>
          )}
          
          {/* Certifications Required */}
          {certifications && certifications.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] text-muted-foreground">Requires:</span>
              {certifications.slice(0, 2).map((cert, i) => (
                <Badge key={i} className="text-[9px] h-5 bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-0">
                  {cert}
                </Badge>
              ))}
            </div>
          )}
        </div>
      );
    }

    // =============================================
    // PARTS RENT REQUEST (parts_rent_request)
    // =============================================
    if (listingType === 'parts_rent_request') {
      return (
        <div className="space-y-2.5 relative z-10">
          {/* SEEKING RENTAL Header + Manufacturer + Model */}
          <div className="flex items-center gap-1.5 flex-wrap justify-between">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white border-0 shadow-sm text-[10px] h-6 px-2 font-bold">
                🔎 SEEKING RENTAL
              </Badge>
              <Badge variant="outline" className="text-[10px] h-5 capitalize bg-background">
                <ItemTypeIcon />
                <span className="ml-1">{itemType || 'Part'}</span>
              </Badge>
              {/* Manufacturer Badge */}
              {(helicopterManufacturer || engineManufacturer) && (
                <Badge variant="outline" className="text-[10px] h-5 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/30 dark:to-red-900/30 border-orange-200 dark:border-orange-700 text-orange-700 dark:text-orange-300 font-semibold">
                  {helicopterManufacturer || engineManufacturer}
                </Badge>
              )}
              {/* Model Badge */}
              {(helicopterModel || engineModel) && (
                <Badge variant="outline" className="text-[10px] h-5 bg-white dark:bg-slate-800 font-medium">
                  {helicopterModel || engineModel}
                </Badge>
              )}
            </div>
          </div>
          
          {/* Unified Compatibility Box */}
          <CompatibilityBox variant="rent_request" />
          
          {/* Rental Duration Needed - prominent display */}
          <div className="p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200/50 dark:border-slate-700/30">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] text-muted-foreground">Duration</div>
                <div className="font-semibold text-sm">
                  {preferredTerm ? `${preferredTerm} ${rentalPeriod || 'day'}s` : minimumTerm ? `${minimumTerm}+ ${rentalPeriod || 'day'}s` : 'Flexible'}
                </div>
              </div>
              {maxRate && (
                <div>
                  <div className="text-[10px] text-muted-foreground">Max Budget</div>
                  <div className="font-semibold text-sm text-orange-600 dark:text-orange-400">
                    ${maxRate.toLocaleString()}/{rentalPeriod || 'day'}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Urgency & Tool Requirements Row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {urgency && (
              <Badge className={cn(
                "text-[10px] h-6 border-0 font-medium",
                urgency === 'immediate' && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
                urgency === 'within_week' && "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
                urgency === 'within_month' && "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
                !['immediate', 'within_week', 'within_month'].includes(urgency) && "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400"
              )}>
                {urgency === 'immediate' && '🔥 Immediate'}
                {urgency === 'within_week' && '📅 Within Week'}
                {urgency === 'within_month' && '📆 Within Month'}
                {!['immediate', 'within_week', 'within_month'].includes(urgency) && urgency.replace(/_/g, ' ')}
              </Badge>
            )}
            {/* Tool calibration requirement */}
            {itemType === 'tool' && (
              <Badge variant="outline" className="text-[10px] h-5 bg-background">
                <Gauge className="w-2.5 h-2.5 mr-0.5" />
                Calibrated required
              </Badge>
            )}
          </div>
          
          {/* Description preview if available */}
          {description && (
            <div className="text-xs text-muted-foreground line-clamp-2 italic">
              "{description.slice(0, 100)}{description.length > 100 ? '...' : ''}"
            </div>
          )}
        </div>
      );
    }

    // =============================================
    // DEFAULT / FALLBACK - Basic parts display
    // =============================================
    return (
      <div className="space-y-2.5 relative z-10">
        {/* Item Type & Part Number */}
        <div className="flex items-center gap-2 flex-wrap">
          {itemType && (
            <Badge variant="outline" className="text-[10px] h-5 capitalize bg-background">
              <ItemTypeIcon />
              <span className="ml-1">{itemType}</span>
            </Badge>
          )}
          {partNumber && (
            <Badge variant="outline" className="text-[10px] font-mono bg-background h-5">
              <Hash className="w-2.5 h-2.5 mr-1" />
              {partNumber}
            </Badge>
          )}
          {manufacturer && (
            <Badge variant="outline" className="text-[10px] h-5 bg-slate-50 dark:bg-slate-800">
              {manufacturer}
            </Badge>
          )}
        </div>
        
        {/* Time/Cycle Data if available */}
        {(tsn || csn) && (
          <div className="grid grid-cols-2 gap-2">
            <LifeIndicator 
              label="Total Time"
              hours={tsn}
              secondary={csn}
              secondaryType="cycles"
            />
          </div>
        )}
        
        {/* Condition & Certifications */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {partsCondition && (
            <Badge variant="outline" className="text-[10px] h-5 capitalize bg-background">
              {partsCondition}
            </Badge>
          )}
          {certifications && certifications.slice(0, 2).map((cert, i) => (
            <Badge key={i} className="text-[9px] h-5 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200/60 dark:border-emerald-700/40">
              {cert}
            </Badge>
          ))}
        </div>
        <ShippingBadges />
      </div>
    );
  };

  // Render job specs - premium styling for job listings
  const renderJobSpecs = () => {
    if (!jobData) return null;
    
    const {
      jobCategory,
      employmentType,
      remoteOptions,
      salaryMin,
      salaryMax,
      salaryType,
      companyName,
      companyLogo,
      currentPosition,
      experienceYearsMin,
      experienceYearsMax,
      flightHoursMin,
      licenses = [],
      certifications = [],
      typeRatings = [],
      medicalClass,
      benefits,
      relocationOffered,
      includesBenefits,
    } = jobData;

    const isJobPost = listingType === 'job_post' || listingType === 'job_contract';
    const isJobProfile = listingType === 'job_profile' || listingType === 'job_seeking';
    
    // Salary formatting uses shared utility (imported at top)

    // Employment type labels
    const employmentLabels: Record<string, string> = {
      full_time: 'Full-Time',
      part_time: 'Part-Time',
      contract: 'Contract',
      freelance: 'Freelance',
      internship: 'Internship',
      seasonal: 'Seasonal',
    };

    // Remote option labels
    const remoteLabels: Record<string, string> = {
      onsite: 'On-Site',
      remote: 'Remote',
      hybrid: 'Hybrid',
    };

    // Company/Profile header for job posts
    if (isJobPost) {
      return (
        <div className="space-y-3 relative z-10">
          {/* Company Header - Logo + Name */}
          <div className="flex items-center gap-3 p-2.5 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-900/30 rounded-xl border border-slate-200/60 dark:border-slate-700/40">
            {companyLogo ? (
              <SmartLogo
                src={companyLogo}
                alt={companyName || 'Company'}
                variant="inline"
                className="w-12 h-12 flex-shrink-0"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm truncate">{companyName || 'Company'}</div>
              <div className="text-xs text-muted-foreground capitalize">
                {jobCategory?.replace(/_/g, ' ') || 'Aviation'}
              </div>
            </div>
          </div>

          {/* Salary & Employment Grid */}
          <div className="grid grid-cols-2 gap-2">
            {/* Salary */}
            <div className="p-2.5 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/40 dark:to-green-950/30 rounded-xl border border-emerald-200/60 dark:border-emerald-700/40">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-bold mb-1 flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                Salary
              </div>
              {shouldHidePrice ? (
                <BlurredPrivateData
                  placeholderContent={<span className="font-bold text-sm text-emerald-800 dark:text-emerald-300">$XX,XXX</span>}
                  fieldName="Salary"
                  size="sm"
                  inline
                  onRequestInfo={isOwner ? undefined : () => requestField('price')}
                />
              ) : (
                <div className="font-bold text-sm text-emerald-800 dark:text-emerald-300">
                  {formatJobSalary(salaryMin, salaryMax, salaryType, 'Competitive', currency)}
                </div>
              )}
            </div>
            
            {/* Employment Type */}
            <div className="p-2.5 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/30 rounded-xl border border-blue-200/60 dark:border-blue-700/40">
              <div className="text-[10px] uppercase tracking-wider text-blue-700 dark:text-blue-400 font-bold mb-1 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Type
              </div>
              <div className="font-bold text-sm text-blue-800 dark:text-blue-300">
                {employmentLabels[employmentType || ''] || employmentType || 'Full-Time'}
              </div>
            </div>
          </div>

          {/* Requirements Section - Type Ratings FIRST */}
          <div className="p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200/50 dark:border-slate-700/30">
            <div className="text-[10px] uppercase tracking-wide text-slate-600 dark:text-slate-400 font-semibold mb-2 flex items-center gap-1.5">
              <Award className="w-3 h-3" />
              Requirements
            </div>
            
            {/* Type Ratings - TOP PRIORITY with helicopter icons from database */}
            {typeRatings.length > 0 && (
              <div className="mb-2.5 p-2 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200/60 dark:border-violet-700/40">
                <div className="text-[9px] uppercase tracking-wide text-violet-600 dark:text-violet-400 font-bold mb-1.5 flex items-center gap-1">
                  <HelicopterSilhouetteIcon className="w-3 h-3" />
                  Type Ratings
                </div>
                <JobQualificationBadges 
                  typeRatings={typeRatings}
                  variant="compact"
                  maxShow={4}
                  showHelicopterIcons
                />
              </div>
            )}
            
            {/* Experience & Hours Grid */}
            {(experienceYearsMin || flightHoursMin) && (
              <div className="grid grid-cols-2 gap-2 mb-2">
                {experienceYearsMin && (
                  <div className="flex items-center gap-2 p-2 bg-white/60 dark:bg-black/20 rounded-lg">
                    <GraduationCap className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[9px] text-muted-foreground leading-tight">Experience</div>
                      <div className="font-bold text-xs tabular-nums">
                        {experienceYearsMin}{experienceYearsMax ? `-${experienceYearsMax}` : '+'} years
                      </div>
                    </div>
                  </div>
                )}
                {flightHoursMin && (
                  <div className="flex items-center gap-2 p-2 bg-white/60 dark:bg-black/20 rounded-lg">
                    <Plane className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[9px] text-muted-foreground leading-tight">Flight Hours</div>
                      <div className="font-bold text-xs tabular-nums">{flightHoursMin.toLocaleString()}+</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Licenses & Certifications */}
            {(licenses.length > 0 || certifications.length > 0) && (
              <div className="flex items-center gap-1 flex-wrap">
                {licenses.slice(0, 2).map((license, i) => (
                  <Badge 
                    key={`lic-${i}`}
                    variant="outline" 
                    className="text-[9px] px-1.5 py-0 bg-primary/5 dark:bg-primary/10 border-primary/20 text-primary font-medium"
                  >
                    <FileCheck className="w-2.5 h-2.5 mr-0.5" />
                    {license}
                  </Badge>
                ))}
                {certifications.slice(0, 2).map((cert, i) => (
                  <Badge 
                    key={`cert-${i}`}
                    variant="outline" 
                    className="text-[9px] px-1.5 py-0 bg-emerald-50/80 dark:bg-emerald-900/30 border-emerald-200/60 dark:border-emerald-700/40 text-emerald-700 dark:text-emerald-400 font-medium"
                  >
                    <Award className="w-2.5 h-2.5 mr-0.5" />
                    {cert}
                  </Badge>
                ))}
                {(licenses.length + certifications.length > 4) && (
                  <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-muted-foreground">
                    +{licenses.length + certifications.length - 4} more
                  </Badge>
                )}
              </div>
            )}
          </div>

          {/* Remote & Benefits Row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {remoteOptions && (
              <Badge 
                variant="outline" 
                className={cn(
                  "text-[10px] px-2 py-0.5",
                  remoteOptions === 'remote' && "bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-400",
                  remoteOptions === 'hybrid' && "bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-700 text-cyan-700 dark:text-cyan-400",
                  remoteOptions === 'onsite' && "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-400"
                )}
              >
                <Globe className="w-3 h-3 mr-1" />
                {remoteLabels[remoteOptions] || remoteOptions}
              </Badge>
            )}
            {includesBenefits && (
              <Badge variant="outline" className="text-[10px] px-2 py-0.5 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-400">
                <Heart className="w-3 h-3 mr-1" />
                Benefits
              </Badge>
            )}
            {relocationOffered && (
              <Badge variant="outline" className="text-[10px] px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-400">
                <Truck className="w-3 h-3 mr-1" />
                Relocation
              </Badge>
            )}
          </div>
        </div>
      );
    }

    // Profile/Seeking layout
    if (isJobProfile) {
      return (
        <div className="space-y-3 relative z-10">
          {/* Professional Header */}
          <div className="flex items-center gap-3 p-2.5 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/30 rounded-xl border border-blue-200/60 dark:border-blue-700/40">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0 ring-2 ring-white dark:ring-slate-700 shadow-md">
              <User className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              {currentPosition && (
                <div className="font-semibold text-sm truncate">{currentPosition}</div>
              )}
              <div className="text-xs text-muted-foreground capitalize">
                {jobCategory?.replace(/_/g, ' ') || 'Aviation Professional'}
              </div>
            </div>
            {listingType === 'job_seeking' && (
              <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-sm text-[10px] h-6 px-2 font-bold flex-shrink-0">
                🔍 Seeking
              </Badge>
            )}
          </div>

          {/* Qualifications Section - Type Ratings FIRST */}
          <div className="p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200/50 dark:border-slate-700/30">
            <div className="text-[10px] uppercase tracking-wide text-slate-600 dark:text-slate-400 font-semibold mb-2 flex items-center gap-1.5">
              <Award className="w-3 h-3" />
              Qualifications
            </div>
            
            {/* Type Ratings - TOP PRIORITY with helicopter icons from database */}
            {typeRatings.length > 0 && (
              <div className="mb-2.5 p-2 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200/60 dark:border-violet-700/40">
                <div className="text-[9px] uppercase tracking-wide text-violet-600 dark:text-violet-400 font-bold mb-1.5 flex items-center gap-1">
                  <Plane className="w-3 h-3" />
                  Type Ratings
                </div>
                <JobQualificationBadges 
                  typeRatings={typeRatings}
                  variant="compact"
                  maxShow={4}
                  showHelicopterIcons
                />
              </div>
            )}

            {/* Experience Grid */}
            {(experienceYearsMin || flightHoursMin) && (
              <div className="grid grid-cols-2 gap-2 mb-2">
                {experienceYearsMin && (
                  <div className="p-2 bg-white/60 dark:bg-black/20 rounded-lg">
                    <div className="flex items-center gap-1.5 mb-1">
                      <GraduationCap className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                      <span className="text-[9px] text-muted-foreground uppercase">Experience</span>
                    </div>
                    <div className="font-bold text-xs">{experienceYearsMin}+ years</div>
                    <div className="mt-1 h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"
                        style={{ width: `${Math.min(100, (experienceYearsMin / 20) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
                {flightHoursMin && (
                  <div className="p-2 bg-white/60 dark:bg-black/20 rounded-lg">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Plane className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                      <span className="text-[9px] text-muted-foreground uppercase">Flight Hours</span>
                    </div>
                    <div className="font-bold text-xs">{flightHoursMin.toLocaleString()}</div>
                    <div className="mt-1 h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-emerald-500 to-green-500 rounded-full"
                        style={{ width: `${Math.min(100, (flightHoursMin / 10000) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Licenses & Certifications */}
            {(licenses.length > 0 || certifications.length > 0 || medicalClass) && (
              <div className="flex items-center gap-1 flex-wrap">
                {medicalClass && (
                  <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-700 dark:text-red-400 font-medium">
                    <Heart className="w-2.5 h-2.5 mr-0.5" />
                    {medicalClass}
                  </Badge>
                )}
                {licenses.slice(0, 2).map((license, i) => (
                  <Badge 
                    key={`lic-${i}`}
                    variant="outline" 
                    className="text-[9px] px-1.5 py-0 bg-primary/5 dark:bg-primary/10 border-primary/20 text-primary font-medium"
                  >
                    <FileCheck className="w-2.5 h-2.5 mr-0.5" />
                    {license}
                  </Badge>
                ))}
                {certifications.slice(0, 2).map((cert, i) => (
                  <Badge 
                    key={`cert-${i}`}
                    variant="outline" 
                    className="text-[9px] px-1.5 py-0 bg-emerald-50/80 dark:bg-emerald-900/30 border-emerald-200/60 dark:border-emerald-700/40 text-emerald-700 dark:text-emerald-400 font-medium"
                  >
                    <Award className="w-2.5 h-2.5 mr-0.5" />
                    {cert}
                  </Badge>
                ))}
                {(licenses.length + certifications.length > 4) && (
                  <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-muted-foreground">
                    +{licenses.length + certifications.length - 4} more
                  </Badge>
                )}
              </div>
            )}
          </div>

          {/* Salary Expectations */}
          {(salaryMin || salaryMax) && (
            <div className="p-2.5 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/30 rounded-xl border border-amber-200/60 dark:border-amber-700/40">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400 font-bold mb-1 flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                Expected Salary
              </div>
              <div className="font-bold text-sm text-amber-800 dark:text-amber-300">
                {formatJobSalary(salaryMin, salaryMax, salaryType, 'Negotiable', currency)}
              </div>
            </div>
          )}

          {/* Preferences Row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {remoteOptions && (
              <Badge 
                variant="outline" 
                className={cn(
                  "text-[10px] px-2 py-0.5",
                  remoteOptions === 'remote' && "bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-400",
                  remoteOptions === 'hybrid' && "bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-700 text-cyan-700 dark:text-cyan-400",
                  remoteOptions === 'onsite' && "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-400"
                )}
              >
                <Globe className="w-3 h-3 mr-1" />
                {remoteLabels[remoteOptions] || 'Flexible'}
              </Badge>
            )}
          </div>
        </div>
      );
    }

    // Default fallback
    return (
      <div className="space-y-2 relative z-10">
        <Badge variant="outline" className="text-[10px] capitalize">
          {jobCategory?.replace(/_/g, ' ') || 'Job'}
        </Badge>
      </div>
    );
  };

  // Render services specs with category-specific premium layouts
  const renderServicesSpecs = () => {
    if (!servicesData) return null;
    
    const {
      category,
      listingType: serviceListingType,
      companyName,
      companyLogo,
      regulatoryApprovals = [],
      certifications = [],
      serviceTypes = [],
      helicopterCapabilityIds = [],
      engineCapabilityIds = [],
      helicopterCapabilities = 0,
      engineCapabilities = 0,
      missionTypes = [],
      responseTime,
      turnaroundTime,
      aogSupport,
      aogResponseTime,
      shiftCoverage,
      facilitySize,
      hangarCapacity,
      fleetSize,
      instructorCount,
      simulatorTypes = [],
      fuelTypes = [],
      amenities = [],
      droneTypes = [],
      flightAuthorizations = [],
      pricingModel,
      serviceRadius,
    } = servicesData;

    const isRequest = serviceListingType === 'service_request' || serviceListingType === 'rfq_request';
    
    // Category labels with icons - uses shared config
    const getCategoryIcon = () => {
      switch (category) {
        case 'mro': return <Wrench className="w-3 h-3" />;
        case 'camo': return <Shield className="w-3 h-3" />;
        case 'part21': return <Award className="w-3 h-3" />;
        case 'training': return <GraduationCap className="w-3 h-3" />;
        case 'operator': return <Plane className="w-3 h-3" />;
        case 'engineering': return <Settings className="w-3 h-3" />;
        case 'ground_ops': return <Truck className="w-3 h-3" />;
        case 'fbo_heliport': return <Building2 className="w-3 h-3" />;
        case 'management': return <Building2 className="w-3 h-3" />;
        case 'uas': return <Plane className="w-3 h-3" />;
        default: return <Settings className="w-3 h-3" />;
      }
    };

    const config = getServiceCategoryBadgeConfig(category || '');

    // SERVICE REQUEST layout
    if (isRequest) {
      return (
        <div className="space-y-3 relative z-10">
          {/* Request Type Badge */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-sm text-[10px] h-6 px-2.5">
              {serviceListingType === 'rfq_request' ? 'RFQ' : 'Service Request'}
            </Badge>
            <Badge className={cn("text-[10px] h-6 px-2 border-0", config.bgColor, config.color)}>
              {getCategoryIcon()}
              <span className="ml-1">{config.label}</span>
            </Badge>
          </div>

          {/* Request Details Card */}
          <div className="p-3 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/30 rounded-xl border border-amber-200/60 dark:border-amber-700/40 shadow-sm">
            <div className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400 font-bold mb-2 flex items-center gap-1.5">
              <Gauge className="w-3 h-3" />
              {serviceListingType === 'rfq_request' ? 'Requesting Quote For' : 'Services Needed'}
            </div>
            {serviceTypes.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {serviceTypes.slice(0, 4).map((service, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] bg-white/60 dark:bg-black/20 border-amber-200/60">
                    {service}
                  </Badge>
                ))}
                {serviceTypes.length > 4 && (
                  <Badge variant="outline" className="text-[10px] text-amber-600 dark:text-amber-400">
                    +{serviceTypes.length - 4} more
                  </Badge>
                )}
              </div>
            ) : (
              <div className="text-xs text-amber-600/80 dark:text-amber-400/80 italic">
                Contact for requirements
              </div>
            )}
          </div>

          {/* Urgency / Timeline if available */}
          {responseTime && (
            <Badge variant="outline" className="text-[10px] bg-red-50/80 dark:bg-red-900/20 border-red-200/60 text-red-700 dark:text-red-400">
              <Clock className="w-3 h-3 mr-1" />
              Needed: {formatResponseTime(responseTime)}
            </Badge>
          )}
        </div>
      );
    }

    // SERVICE OFFER layout - Category-specific premium displays
    const renderCategorySpecificContent = () => {
      switch (category) {
        // MRO Layout - Focus on AOG, facility, approvals, and capabilities
        case 'mro':
          return (
            <>
              {/* Helicopter & Engine Capabilities with Icons - TOP PRIORITY for MRO */}
              {(helicopterCapabilityIds.length > 0 || engineCapabilityIds.length > 0) && (
                <div className="p-2.5 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/20 rounded-lg border border-violet-200/50 dark:border-violet-700/30">
                  <div className="text-[10px] uppercase tracking-wide text-violet-600 dark:text-violet-400 font-bold mb-1.5 flex items-center gap-1">
                    <Plane className="w-3 h-3" />
                    Approved Aircraft & Engines
                  </div>
                  <ServiceCapabilityBadges 
                    helicopterCapabilityIds={helicopterCapabilityIds}
                    engineCapabilityIds={engineCapabilityIds}
                    variant="compact"
                    maxShow={4}
                  />
                </div>
              )}
              {/* AOG Support Card */}
              {aogSupport && (
                <div className="p-2.5 bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/30 dark:to-orange-950/20 rounded-lg border border-red-200/50 dark:border-red-700/30">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-full bg-red-500/20">
                      <Zap className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-red-700 dark:text-red-400">AOG Support 24/7</div>
                      {aogResponseTime && <div className="text-[9px] text-red-600/70 dark:text-red-400/70">Response: {formatResponseTime(aogResponseTime)}</div>}
                    </div>
                  </div>
                </div>
              )}
              {/* Facility Stats */}
              {(facilitySize || hangarCapacity || shiftCoverage) && (
                <div className="flex items-center gap-2 flex-wrap">
                  {facilitySize && (
                    <Badge variant="outline" className="text-[10px] bg-slate-50 dark:bg-slate-800">
                      <Building2 className="w-3 h-3 mr-1" />
                      {facilitySize.toLocaleString()} m²
                    </Badge>
                  )}
                  {hangarCapacity && (
                    <Badge variant="outline" className="text-[10px] bg-slate-50 dark:bg-slate-800">
                      <Plane className="w-3 h-3 mr-1" />
                      {hangarCapacity} aircraft
                    </Badge>
                  )}
                  {shiftCoverage && (
                    <Badge variant="outline" className="text-[10px]">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatShiftCoverage(shiftCoverage)}
                    </Badge>
                  )}
                </div>
              )}
            </>
          );

        // Training Layout - Focus on instructors, simulators, fleet, and capabilities
        case 'training':
          return (
            <>
              {/* Helicopter & Engine Capabilities - Important for Training */}
              {(helicopterCapabilityIds.length > 0 || engineCapabilityIds.length > 0) && (
                <div className="p-2.5 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/20 rounded-lg border border-violet-200/50 dark:border-violet-700/30">
                  <div className="text-[10px] uppercase tracking-wide text-violet-600 dark:text-violet-400 font-bold mb-1.5 flex items-center gap-1">
                    <Plane className="w-3 h-3" />
                    Training Aircraft
                  </div>
                  <ServiceCapabilityBadges 
                    helicopterCapabilityIds={helicopterCapabilityIds}
                    engineCapabilityIds={engineCapabilityIds}
                    variant="compact"
                    maxShow={4}
                  />
                </div>
              )}
              {/* Training Stats Card */}
              {(instructorCount || fleetSize || simulatorTypes.length > 0) && (
                <div className="p-2.5 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/20 rounded-lg border border-green-200/50 dark:border-green-700/30">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    {instructorCount && (
                      <div>
                        <div className="text-lg font-bold text-green-700 dark:text-green-400">{instructorCount}</div>
                        <div className="text-[9px] text-green-600/70 dark:text-green-400/70">Instructors</div>
                      </div>
                    )}
                    {fleetSize && (
                      <div>
                        <div className="text-lg font-bold text-green-700 dark:text-green-400">{fleetSize}</div>
                        <div className="text-[9px] text-green-600/70 dark:text-green-400/70">Aircraft</div>
                      </div>
                    )}
                    {simulatorTypes.length > 0 && (
                      <div>
                        <div className="text-lg font-bold text-green-700 dark:text-green-400">{simulatorTypes.length}</div>
                        <div className="text-[9px] text-green-600/70 dark:text-green-400/70">Simulators</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          );

        // CAMO Layout - Focus on approvals, capabilities, and regions
        case 'camo':
          return (
            <>
              {/* Helicopter & Engine Capabilities - Critical for CAMO */}
              {(helicopterCapabilityIds.length > 0 || engineCapabilityIds.length > 0) && (
                <div className="p-2.5 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/20 rounded-lg border border-violet-200/50 dark:border-violet-700/30">
                  <div className="text-[10px] uppercase tracking-wide text-violet-600 dark:text-violet-400 font-bold mb-1.5 flex items-center gap-1">
                    <Plane className="w-3 h-3" />
                    Managed Aircraft Types
                  </div>
                  <ServiceCapabilityBadges 
                    helicopterCapabilityIds={helicopterCapabilityIds}
                    engineCapabilityIds={engineCapabilityIds}
                    variant="compact"
                    maxShow={4}
                  />
                </div>
              )}
              {/* Response / Coverage */}
              <div className="flex items-center gap-2 flex-wrap">
                {responseTime && (
                  <Badge variant="outline" className="text-[10px]">
                    <Clock className="w-3 h-3 mr-1" />
                    {formatResponseTime(responseTime)}
                  </Badge>
                )}
                {serviceRadius && (
                  <Badge variant="outline" className="text-[10px]">
                    <Globe className="w-3 h-3 mr-1" />
                    {serviceRadius} km coverage
                  </Badge>
                )}
              </div>
            </>
          );

        // Operator Layout - Focus on fleet, missions, and capabilities
        case 'operator':
          return (
            <>
              {/* Helicopter & Engine Capabilities - Important for Operators */}
              {(helicopterCapabilityIds.length > 0 || engineCapabilityIds.length > 0) && (
                <div className="p-2.5 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/20 rounded-lg border border-violet-200/50 dark:border-violet-700/30">
                  <div className="text-[10px] uppercase tracking-wide text-violet-600 dark:text-violet-400 font-bold mb-1.5 flex items-center gap-1">
                    <Plane className="w-3 h-3" />
                    Fleet Aircraft
                  </div>
                  <ServiceCapabilityBadges 
                    helicopterCapabilityIds={helicopterCapabilityIds}
                    engineCapabilityIds={engineCapabilityIds}
                    variant="compact"
                    maxShow={4}
                  />
                </div>
              )}
              {/* Fleet & Mission Stats */}
              <div className="flex items-center gap-2 flex-wrap">
                {fleetSize && (
                  <Badge className="text-[10px] bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 border-0">
                    <Plane className="w-3 h-3 mr-1" />
                    {fleetSize} aircraft fleet
                  </Badge>
                )}
                {serviceRadius && (
                  <Badge variant="outline" className="text-[10px]">
                    <Globe className="w-3 h-3 mr-1" />
                    {serviceRadius} km range
                  </Badge>
                )}
              </div>
              {/* Mission Types */}
              {missionTypes.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {missionTypes.slice(0, 4).map((mission, i) => (
                    <Badge key={i} variant="outline" className="text-[9px] bg-sky-50/80 dark:bg-sky-900/20 border-sky-200/60 text-sky-700 dark:text-sky-300">
                      {mission}
                    </Badge>
                  ))}
                  {missionTypes.length > 4 && (
                    <Badge variant="outline" className="text-[9px] text-muted-foreground">
                      +{missionTypes.length - 4}
                    </Badge>
                  )}
                </div>
              )}
            </>
          );

        // FBO/Heliport Layout - Focus on fuel, amenities
        case 'fbo_heliport':
          return (
            <>
              {/* Fuel Types */}
              {fuelTypes.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {fuelTypes.map((fuel, i) => (
                    <Badge key={i} className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-0">
                      {fuel}
                    </Badge>
                  ))}
                </div>
              )}
              {/* Amenities Grid */}
              {amenities.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {amenities.slice(0, 5).map((amenity, i) => (
                    <Badge key={i} variant="outline" className="text-[9px] bg-indigo-50/80 dark:bg-indigo-900/20">
                      <Check className="w-3 h-3 mr-0.5" />
                      {amenity}
                    </Badge>
                  ))}
                  {amenities.length > 5 && (
                    <Badge variant="outline" className="text-[9px] text-muted-foreground">
                      +{amenities.length - 5}
                    </Badge>
                  )}
                </div>
              )}
            </>
          );

        // UAS/Drone Layout - Focus on drone types, authorizations
        case 'uas':
          return (
            <>
              {/* Drone Types */}
              {droneTypes.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {droneTypes.slice(0, 3).map((drone, i) => (
                    <Badge key={i} className="text-[10px] bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 border-0">
                      {drone}
                    </Badge>
                  ))}
                  {droneTypes.length > 3 && (
                    <Badge variant="outline" className="text-[9px] text-muted-foreground">
                      +{droneTypes.length - 3}
                    </Badge>
                  )}
                </div>
              )}
              {/* Flight Authorizations */}
              {flightAuthorizations.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {flightAuthorizations.slice(0, 3).map((auth, i) => (
                    <Badge key={i} variant="outline" className="text-[9px] bg-emerald-50/80 dark:bg-emerald-900/20 border-emerald-200/60 text-emerald-700 dark:text-emerald-400">
                      <Check className="w-3 h-3 mr-0.5" />
                      {auth}
                    </Badge>
                  ))}
                </div>
              )}
            </>
          );

        // Default layout for other categories - now with capability badges
        default:
          return (
            <>
              {/* Helicopter & Engine Capabilities with Icons */}
              {(helicopterCapabilityIds.length > 0 || engineCapabilityIds.length > 0) ? (
                <div className="p-2.5 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/20 rounded-lg border border-violet-200/50 dark:border-violet-700/30">
                  <div className="text-[10px] uppercase tracking-wide text-violet-600 dark:text-violet-400 font-bold mb-1.5 flex items-center gap-1">
                    <Plane className="w-3 h-3" />
                    Approved Aircraft & Engines
                  </div>
                  <ServiceCapabilityBadges 
                    helicopterCapabilityIds={helicopterCapabilityIds}
                    engineCapabilityIds={engineCapabilityIds}
                    variant="compact"
                    maxShow={4}
                  />
                </div>
              ) : (helicopterCapabilities > 0 || engineCapabilities > 0) && (
                <div className="flex items-center gap-2 flex-wrap">
                  {helicopterCapabilities > 0 && (
                    <Badge variant="outline" className="text-[10px] bg-blue-50 dark:bg-blue-900/20 border-blue-200/60 text-blue-700 dark:text-blue-300">
                      <Plane className="w-3 h-3 mr-1" />
                      {helicopterCapabilities} types
                    </Badge>
                  )}
                  {engineCapabilities > 0 && (
                    <Badge variant="outline" className="text-[10px] bg-slate-50 dark:bg-slate-800">
                      <Settings className="w-3 h-3 mr-1" />
                      {engineCapabilities} engines
                    </Badge>
                  )}
                </div>
              )}
              {/* Response time */}
              {responseTime && (
                <Badge variant="outline" className="text-[10px]">
                  <Clock className="w-3 h-3 mr-1" />
                  {formatResponseTime(responseTime)}
                </Badge>
              )}
            </>
          );
      }
    };

    return (
      <div className="space-y-3 relative z-10">
        {/* Category Badge + Type Badge */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={cn("text-[10px] h-6 px-2.5 border-0 shadow-sm", config.bgColor, config.color)}>
            {getCategoryIcon()}
            <span className="ml-1">{config.label}</span>
          </Badge>
          <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0 shadow-sm text-[10px] h-6 px-2">
            {serviceListingType === 'capability_listing' ? 'Capability' : 'Provider'}
          </Badge>
        </div>

        {/* Regulatory Approvals - Premium Display */}
        {regulatoryApprovals.length > 0 && (
          <div className="p-2.5 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/20 rounded-lg border border-emerald-200/50 dark:border-emerald-700/30">
            <div className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400 font-semibold mb-1.5 flex items-center gap-1">
              <Shield className="w-3 h-3" />
              Verified Approvals
            </div>
            <div className="flex flex-wrap gap-1.5">
              {regulatoryApprovals.slice(0, 4).map((approval, i) => (
                <Badge key={i} className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-800 dark:text-emerald-200 border-0">
                  <Check className="w-3 h-3 mr-1" />
                  {approval}
                </Badge>
              ))}
              {regulatoryApprovals.length > 4 && (
                <Badge variant="outline" className="text-[10px] text-emerald-600 dark:text-emerald-400">
                  +{regulatoryApprovals.length - 4}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Category-Specific Content */}
        {renderCategorySpecificContent()}

        {/* Services Offered - Compact */}
        {serviceTypes.length > 0 && !['training', 'operator'].includes(category || '') && (
          <div className="flex flex-wrap gap-1">
            {serviceTypes.slice(0, 3).map((service, i) => (
              <Badge key={i} variant="outline" className="text-[9px] bg-background">
                {service}
              </Badge>
            ))}
            {serviceTypes.length > 3 && (
              <Badge variant="outline" className="text-[9px] text-muted-foreground">
                +{serviceTypes.length - 3}
              </Badge>
            )}
          </div>
        )}

        {/* Additional Certifications - Separated */}
        {certifications.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-2 border-t border-border/40">
            {certifications.slice(0, 3).map((cert, i) => (
              <Badge key={i} variant="outline" className="text-[9px] bg-blue-50/80 dark:bg-blue-900/20 border-blue-200/60 text-blue-700 dark:text-blue-300">
                {cert}
              </Badge>
            ))}
            {certifications.length > 3 && (
              <Badge variant="outline" className="text-[9px] text-muted-foreground">
                +{certifications.length - 3}
              </Badge>
            )}
          </div>
        )}
      </div>
    );
  };

  // Unified specs renderer
  const renderSpecs = () => {
    switch (assetType) {
      case 'helicopter':
        return renderHelicopterSpecs();
      case 'engine':
        return renderEngineSpecs();
      case 'parts':
        return renderPartsSpecs();
      case 'jobs':
        return renderJobSpecs();
      case 'services':
        return renderServicesSpecs();
      default:
        return null;
    }
  };

  // Status badge for management mode
  const renderStatusBadge = () => {
    if (!isManagement || !listingStatus) return null;
    
    const statusColors: Record<string, string> = {
      active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      inactive: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
      pending: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    };
    
    return (
      <Badge className={cn('text-xs capitalize', statusColors[listingStatus] || statusColors.draft)}>
        {listingStatus}
      </Badge>
    );
  };

  // Management action buttons
  const renderManagementActions = () => {
    if (!isManagement) return null;
    
    return (
      <div className="flex flex-wrap gap-1.5 pt-2 border-t border-border/50">
        <Button
          size="sm"
          variant="outline"
          className="flex-1 h-8 text-xs gap-1"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails?.();
          }}
        >
          <Eye className="w-3 h-3" />
          View
        </Button>
        
        {onEdit && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs gap-1"
            aria-label="Edit listing"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            data-tutorial-id="listing-edit-button"
          >
            <Edit className="w-3 h-3" />
          </Button>
        )}
        
        {onToggleStatus && (
          <Button
            size="sm"
            variant="outline"
            className={cn(
              "h-8 text-xs gap-1",
              isActive && "text-yellow-600 hover:text-yellow-700 dark:text-yellow-400 dark:hover:text-yellow-300",
              isPaused && "text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
            )}
            aria-label={isActive ? "Pause listing" : "Activate listing"}
            onClick={(e) => {
              e.stopPropagation();
              onToggleStatus();
            }}
            data-tutorial-id="listing-status-toggle"
          >
            {isActive ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
          </Button>
        )}
        
        {onPrivacy && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs gap-1"
            aria-label="Privacy settings"
            onClick={(e) => {
              e.stopPropagation();
              onPrivacy();
            }}
            data-tutorial-id="listing-privacy-button"
          >
            <Shield className="w-3 h-3" />
          </Button>
        )}
        
        {onDelete && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs gap-1 text-destructive hover:text-destructive"
                aria-label="Delete listing"
                onClick={(e) => e.stopPropagation()}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent onClick={(e) => e.stopPropagation()}>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Listing</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this listing? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    );
  };

  // Default action buttons (non-management)
  const renderDefaultActions = () => {
    if (isManagement) return null;
    
    return (
      <div className="flex gap-2 border-t border-border/40 pt-3">
        <Button 
          size="sm"
          variant="default"
          className="flex-1 h-10 text-xs gap-1.5 font-semibold shadow-sm"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails?.();
          }}
        >
          <Eye className="w-3.5 h-3.5" />
          View Details
        </Button>
        {sellerId && (
          <Button 
            variant="outline" 
            size="sm" 
            className="w-10 h-10 p-0 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
            aria-label="Message seller"
            onClick={handleMessageSeller}
          >
            <MessageCircle className="w-4 h-4" />
          </Button>
        )}
      </div>
    );
  };

  // Horizontal List View Layout
  if (listView) {
    return (
      <Card
        className={cn(
          "group cursor-pointer overflow-hidden touch-pan-y hover:shadow-lg transition-all duration-300",
          "border border-border/40 bg-card",
          isSelected && "ring-2 ring-primary",
          className
        )}
        onClick={onClick}
      >
        <div className="flex">
          {/* Left: Image - wider responsive width with object-contain to show full asset */}
          <div className="relative w-48 sm:w-60 lg:w-72 flex-shrink-0 bg-card dark:bg-background aspect-[4/3] min-h-[120px]">
            {hasPrimaryImage ? (
              <OptimizedImage
                src={primaryImage}
                alt={title}
                className="w-full h-full object-contain p-2"
              />
            ) : (
              <BrandedListingFallback />
            )}
            
            <div className="absolute top-2 left-2 flex flex-col gap-1">
              <ListingTypeBadge listingType={listingType} itemType={partsData?.itemType} specificityType={partsData?.specificityType} className="text-xs px-2 py-0.5" />
              {renderStatusBadge()}
            </div>

            {featured && (
              <div className="absolute top-2 right-2">
                <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-xs px-2 py-0.5">
                  <Crown className="w-3 h-3 mr-1" />
                  Featured
                </Badge>
              </div>
            )}

            {totalImages > 1 && (
              <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                📷 {totalImages}
              </div>
            )}
          </div>

          {/* Right: Content */}
          <div className="flex-1 flex p-4 gap-4">
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <div>
                <h3 className="font-semibold text-base line-clamp-1 mb-1">{title}</h3>
                <div className="flex items-center gap-3 text-sm text-muted-foreground mb-2">
                  {location && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{location}</span>
                    </div>
                  )}
                  {year && <span className="text-xs">{year}</span>}
                  {condition && (
                    <Badge variant="outline" className="text-xs capitalize">{condition}</Badge>
                  )}
                </div>
              </div>
              {/* Compact specs for list view - listing-type-specific */}
              <div className="flex-1">
                {assetType === 'helicopter' && helicopterData && (
                  <div className="flex flex-col gap-2">
                    {/* FOR BUY REQUEST - Show requirements instead of LifeIndicator */}
                    {isBuyRequest ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        {helicopterData.maxAge && (
                          <div className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-1 rounded-md">
                            <Calendar className="w-3 h-3" />
                            <span>Max {helicopterData.maxAge} yrs</span>
                          </div>
                        )}
                        {helicopterData.maxFlightHours && (
                          <div className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-1 rounded-md">
                            <Clock className="w-3 h-3" />
                            <span>Max {helicopterData.maxFlightHours.toLocaleString()} hrs</span>
                          </div>
                        )}
                        {helicopterData.urgencyLevel && (
                          <Badge variant="outline" className={cn(
                            "text-[10px] capitalize",
                            helicopterData.urgencyLevel === 'urgent' && "border-red-300 text-red-600 bg-red-50 dark:border-red-700 dark:text-red-400 dark:bg-red-900/30"
                          )}>
                            {helicopterData.urgencyLevel === 'urgent' ? '🔥' : '📅'} {helicopterData.urgencyLevel}
                          </Badge>
                        )}
                        {helicopterData.configurationName && (
                          <ConfigurationBadge configuration={helicopterData.configurationName} />
                        )}
                      </div>
                    ) : (
                      <>
                        {/* Standard listings - LifeIndicator row */}
                        <div className="flex items-center gap-3">
                          {helicopterData.airframeHours && (
                            <LifeIndicator 
                              label="Airframe" 
                              hours={helicopterData.airframeHours}
                              secondary={helicopterData.airframeLandings ?? helicopterData.airframeCycles}
                              secondaryType="landings"
                              compact 
                            />
                          )}
                          {helicopterData.engineHours && (
                            <LifeIndicator 
                              label="Engine" 
                              hours={helicopterData.engineHours} 
                              secondary={helicopterData.engineCycles}
                              secondaryType="cycles"
                              compact 
                            />
                          )}
                          {helicopterData.configurationName && (
                            <ConfigurationBadge configuration={helicopterData.configurationName} />
                          )}
                        </div>
                        
                        {/* WET LEASE - Show included services inline */}
                        {listingType === 'wet_lease' && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {helicopterData.pilotProvided && (
                              <Badge className="text-[9px] bg-emerald-100 text-emerald-700 dark:bg-emerald-800 dark:text-emerald-200 border-0 gap-0.5">
                                <Users className="w-2.5 h-2.5" />
                                Crew
                              </Badge>
                            )}
                            {helicopterData.includedServices?.slice(0, 2).map((service, i) => (
                              <Badge key={i} className="text-[9px] bg-emerald-100 text-emerald-700 dark:bg-emerald-800 dark:text-emerald-200 border-0 gap-0.5">
                                <Check className="w-2.5 h-2.5" />
                                {service}
                              </Badge>
                            ))}
                            {(helicopterData.includedServices?.length || 0) > 2 && (
                              <span className="text-[9px] text-emerald-600 dark:text-emerald-400">+{(helicopterData.includedServices?.length || 0) - 2} more</span>
                            )}
                          </div>
                        )}
                        
                        {/* DRY LEASE - Show "Aircraft Only" badge */}
                        {listingType === 'dry_lease' && (
                          <Badge className="text-[9px] bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-200 border-0 w-fit gap-0.5">
                            <Plane className="w-2.5 h-2.5" />
                            Aircraft Only
                          </Badge>
                        )}
                        
                        {/* Equipment badges - use same component as grid view */}
                        {helicopterData.selectedEquipment && helicopterData.selectedEquipment.length > 0 && (
                          <EquipmentBadges 
                            equipment={helicopterData.selectedEquipment} 
                            maxShow={6}
                            compact
                          />
                        )}
                      </>
                    )}
                  </div>
                )}
                {assetType === 'engine' && renderEngineSpecs()}
                {assetType === 'parts' && partsData && (
                  <div className="flex flex-col gap-1.5">
                    {/* Row 1: Item Type + Manufacturer + Part Number */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {partsData.itemType && (
                        <Badge variant="outline" className="text-[10px] h-5 capitalize bg-background gap-0.5">
                          {partsData.itemType === 'part' && <Package className="w-2.5 h-2.5" />}
                          {partsData.itemType === 'tool' && <Wrench className="w-2.5 h-2.5" />}
                          {partsData.itemType === 'equipment' && <Settings className="w-2.5 h-2.5" />}
                          <span className="ml-0.5">{partsData.itemType}</span>
                        </Badge>
                      )}
                      {partsData.manufacturer && (
                        <Badge variant="outline" className="text-[10px] h-5 bg-slate-50 dark:bg-slate-800 font-medium">
                          {partsData.manufacturer}
                        </Badge>
                      )}
                      {partsData.partNumber && (
                        <Badge variant="outline" className="text-[10px] h-5 font-mono bg-background gap-0.5">
                          <Hash className="w-2.5 h-2.5" />
                          {partsData.partNumber}
                        </Badge>
                      )}
                      {partsData.quantity && partsData.quantity > 1 && (
                        <Badge className="text-[9px] h-4 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 border-0">
                          Qty: {partsData.quantity}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Row 2: Compatibility */}
                    {partsData.specificityType && partsData.specificityType !== 'general' && (
                      <Badge variant="outline" className="text-[10px] h-5 bg-background gap-0.5 w-fit">
                        <Plane className="w-2.5 h-2.5" />
                        {partsData.helicopterManufacturer && partsData.helicopterModel 
                          ? `${partsData.helicopterManufacturer} ${partsData.helicopterModel}`
                          : partsData.helicopterModel || partsData.engineModel || partsData.specificityType}
                      </Badge>
                    )}
                    
                    {/* Row 2: Type-specific compact info */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {isBuyRequest ? (
                        /* BUY REQUEST: Budget + Urgency */
                        <>
                          {partsData.maxBudget && (
                            <div className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded-md">
                              <DollarSign className="w-3 h-3" />
                              <span>Up to ${partsData.maxBudget.toLocaleString()}</span>
                            </div>
                          )}
                          {partsData.urgency && (
                            <Badge variant="outline" className={cn(
                              "text-[10px] h-5 capitalize",
                              partsData.urgency === 'immediate' && "border-red-300 text-red-600 bg-red-50 dark:bg-red-900/30 dark:border-red-700 dark:text-red-400"
                            )}>
                              {partsData.urgency === 'immediate' ? '🔥' : '📅'} {partsData.urgency}
                            </Badge>
                          )}
                        </>
                      ) : listingType === 'parts_rent' || listingType === 'parts_rent_request' ? (
                        /* RENT: Rental rate + Deposit/Period + Calibration */
                        <>
                          {partsData.rentalRate && (
                            <div className="flex items-center gap-1 text-xs text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-md">
                              <Clock className="w-3 h-3" />
                              <span>${partsData.rentalRate}/{partsData.rentalPeriod || 'day'}</span>
                            </div>
                          )}
                          {partsData.deposit && (
                            <Badge variant="outline" className="text-[10px] h-5 bg-background">
                              Deposit: ${partsData.deposit.toLocaleString()}
                            </Badge>
                          )}
                          {partsData.itemType === 'tool' && partsData.calibrationStatus && (
                            <Badge className={cn(
                              "text-[10px] h-5 border-0",
                              partsData.calibrationStatus === 'current' && "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400",
                              partsData.calibrationStatus === 'due_soon' && "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400",
                              partsData.calibrationStatus === 'expired' && "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400"
                            )}>
                              Cal: {partsData.calibrationStatus.replace(/_/g, ' ')}
                            </Badge>
                          )}
                        </>
                      ) : (
                        /* SELL: TSN/CSN compact + Certifications */
                        <>
                          {(partsData.tsn !== undefined || partsData.csn !== undefined) && (
                            <LifeIndicator 
                              label="Time"
                              hours={partsData.tsn}
                              secondary={partsData.csn}
                              secondaryType="cycles"
                              compact
                            />
                          )}
                          {partsData.certifications && partsData.certifications.length > 0 && (
                            <div className="flex items-center gap-1">
                              {partsData.certifications.slice(0, 2).map((cert, i) => (
                                <Badge key={i} variant="outline" className="text-[9px] h-4 bg-background">
                                  {cert}
                                </Badge>
                              ))}
                              {partsData.certifications.length > 2 && (
                                <span className="text-[9px] text-muted-foreground">+{partsData.certifications.length - 2}</span>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    
                    {/* Row 3: Shipping options (for sell) */}
                    {!isBuyRequest && listingType?.includes('sell') && (
                      <div className="flex items-center gap-1 flex-wrap">
                        {partsData.localPickupAvailable && (
                          <Badge variant="outline" className="text-[9px] h-4 bg-background gap-0.5">
                            <MapPin className="w-2 h-2" />
                            Local
                          </Badge>
                        )}
                        {partsData.domesticShipping && (
                          <Badge variant="outline" className="text-[9px] h-4 bg-background gap-0.5">
                            <Truck className="w-2 h-2" />
                            Domestic
                          </Badge>
                        )}
                        {partsData.internationalShipping && (
                          <Badge variant="outline" className="text-[9px] h-4 bg-background gap-0.5">
                            <Globe className="w-2 h-2" />
                            Intl
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Price & Actions */}
            <div className="flex flex-col justify-between items-end min-w-[180px]">
              <div className="text-right">
                {shouldHidePrice ? (
                  <BlurredPrivateData
                    placeholderContent={<span className={cn(
                      "text-xl font-bold",
                      isBuyRequest ? "text-[hsl(45,60%,32%)] dark:text-[hsl(45,70%,65%)]" : "text-slate-800 dark:text-slate-200"
                    )}>$XX,XXX</span>}
                    fieldName="Price"
                    size="md"
                    inline
                    onRequestInfo={isOwner ? undefined : () => requestField('price')}
                  />
                ) : (
                  <>
                    {isBuyRequest && <span className="text-[10px] text-[hsl(45,55%,42%)] dark:text-[hsl(45,60%,60%)] block uppercase tracking-wide font-medium mb-0.5">Budget</span>}
                    <div className={cn(
                      "text-xl font-bold",
                      isBuyRequest ? "text-[hsl(45,60%,32%)] dark:text-[hsl(45,70%,65%)]" : "text-slate-800 dark:text-slate-200"
                    )}>{formattedPrice}</div>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm"
                  variant="default"
                  className="gap-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails?.();
                  }}
                >
                  <Eye className="w-3.5 h-3.5" />
                  View
                </Button>
                {isManagement && onEdit && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-9 h-9 p-0"
                    aria-label="Edit listing"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit();
                    }}
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </Button>
                )}
                {!isManagement && sellerId && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-9 h-9 p-0"
                    aria-label="Message seller"
                    onClick={handleMessageSeller}
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "w-9 h-9 p-0",
                    isListingFavorite && "text-red-500 border-red-200 bg-red-50"
                  )}
                  aria-label={isListingFavorite ? "Remove from favorites" : "Add to favorites"}
                  onClick={handleFavoriteClick}
                >
                  <Heart className={cn(
                    "w-3.5 h-3.5",
                    isListingFavorite && "fill-current"
                  )} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Mobile Compact View Layout - Simplified for smaller screens
  if (mobileCompact) {
    const isCompanyJobListing = listingType === 'job_post' || listingType === 'job_contract';
    const isProviderServiceListing = listingType === 'service_offer' || listingType === 'capability_listing';
    const compactImageUrl = assetType === 'jobs'
      ? jobData?.heroImage || jobData?.companyLogo
      : assetType === 'services'
        ? servicesData?.companyLogo || images?.[0]
        : primaryImage;

    const renderCompactVisual = () => {
      if (!compactImageUrl || (!hasPrimaryImage && assetType !== 'jobs' && assetType !== 'services')) {
        return <BrandedListingFallback />;
      }
      if (assetType === 'jobs' && compactImageUrl) {
        if (isCompanyJobListing) {
          return <SmartLogo src={compactImageUrl} alt={jobData?.companyName || title} variant="card" />;
        }
        return (
          <div className="flex h-full w-full items-center justify-center bg-muted/70">
            <div className="h-14 w-14 sm:h-16 sm:w-16 overflow-hidden rounded-full border-2 border-background shadow-lg">
              <img
                src={compactImageUrl}
                alt={title}
                className="h-full w-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).src = heroPlaceholder; }}
              />
            </div>
          </div>
        );
      }
      if (assetType === 'services' && compactImageUrl && isProviderServiceListing) {
        return <SmartLogo src={compactImageUrl} alt={servicesData?.companyName || title} variant="card" />;
      }
      return (
        <OptimizedImage
          src={compactImageUrl}
          alt={title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
      );
    };

    // Framed price box (replaces flat inline text)
    const priceFrameBase = "inline-flex h-7 min-h-7 max-h-7 w-full max-w-full box-border items-center justify-start gap-1.5 overflow-hidden whitespace-nowrap rounded-md border px-2 py-0 leading-none shadow-[0_1px_0_rgba(0,0,0,0.02)] appearance-none m-0 align-middle";
    const priceFrameTone = isBuyRequest
      ? "border-accent/30 bg-accent/10"
      : "border-primary/25 bg-primary/5";

    const priceLabel = isBuyRequest ? 'Budget' : assetType === 'jobs' ? 'Salary' : assetType === 'services' ? 'Rate' : 'Price';

    const renderInlinePrice = () => {
      if (shouldHidePrice) {
        return (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); if (!isOwner) requestField('price'); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); if (!isOwner) requestField('price'); } }}
            className={cn(priceFrameBase, priceFrameTone, "cursor-pointer select-none")}
            aria-label="Request price"
          >
            <span className="inline-flex h-full items-center text-[8.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground leading-none">{priceLabel}</span>
            <span className={cn(
              "inline-flex h-full items-center gap-0.5 text-[12.5px] sm:text-[13.5px] font-bold tracking-tight leading-none",
              isBuyRequest ? "text-accent-foreground" : "text-primary"
            )}>
              <Lock className="h-3 w-3 shrink-0" />
              Locked
            </span>
          </span>
        );
      }
      if (getFormattedPrice.displayType === 'quote') {
        return (
          <span className={cn(priceFrameBase, priceFrameTone)}>
            <span className="inline-flex h-full items-center text-[8.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground leading-none">{priceLabel}</span>
            <span className={cn(
              "inline-flex h-full items-center gap-0.5 text-[12.5px] sm:text-[13.5px] font-bold tracking-tight leading-none",
              isBuyRequest ? "text-accent-foreground" : "text-primary"
            )}>
              <FileText className="h-3 w-3" />
              Quote
            </span>
          </span>
        );
      }
      if (getFormattedPrice.displayType === 'contact') {
        return (
          <span className={cn(priceFrameBase, priceFrameTone)}>
            <span className="inline-flex h-full items-center text-[8.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground leading-none">{priceLabel}</span>
            <span className="inline-flex h-full items-center text-[12.5px] sm:text-[13.5px] font-bold tracking-tight text-muted-foreground leading-none">Contact</span>
          </span>
        );
      }
      if (getFormattedPrice.displayType === 'none' && assetType === 'jobs') {
        return (
          <span className={cn(priceFrameBase, priceFrameTone)}>
            <span className="inline-flex h-full items-center text-[8.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground leading-none">{priceLabel}</span>
            <span className="inline-flex h-full items-center text-[12.5px] sm:text-[13.5px] font-bold tracking-tight text-muted-foreground leading-none">—</span>
          </span>
        );
      }
      return (
        <span className={cn(priceFrameBase, priceFrameTone)}>
          <span className="inline-flex h-full items-center text-[8.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground leading-none">{priceLabel}</span>
          <span className={cn(
            "inline-flex h-full items-center text-[12.5px] sm:text-[13.5px] font-bold tracking-tight tabular-nums leading-none",
            isBuyRequest ? "text-accent-foreground" : "text-primary"
          )}>{formattedPrice}</span>
        </span>
      );
    };

    // Structured spec row — fixed 28px height so frames are pixel-identical across cards
    const SpecRow: React.FC<{ label?: string; value?: React.ReactNode; icon?: React.ReactNode; primary?: boolean; placeholder?: boolean }> = ({ label, value, icon, primary, placeholder }) => (
      <div className={cn(
        "flex h-7 items-center justify-between gap-2 px-2 min-w-0",
        primary && !placeholder && "bg-primary/[0.04]",
        placeholder && "bg-muted/10"
      )}>
        <span className={cn(
          "inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-[0.06em] text-muted-foreground shrink-0",
          placeholder && "opacity-0"
        )}>
          {icon}
          {label || '—'}
        </span>
        <span className={cn(
          "truncate text-[10.5px] font-semibold tabular-nums text-right",
          primary && !placeholder ? "text-primary" : "text-foreground",
          placeholder && "opacity-0"
        )}>
          {value || '—'}
        </span>
      </div>
    );

    const specIcon = "h-2.5 w-2.5";

    // Always render exactly 3 rows — pad missing with invisible placeholders for identical heights
    const SpecFrame: React.FC<{ rows: Array<React.ReactNode | null | false> }> = ({ rows }) => {
      const visible = rows.filter(Boolean) as React.ReactNode[];
      const padded = [...visible];
      while (padded.length < 3) padded.push(<SpecRow key={`pad-${padded.length}`} placeholder />);
      return (
        <div className="rounded-lg border border-border/50 bg-muted/20 divide-y divide-border/40 overflow-hidden">
          {padded.slice(0, 3).map((row, i) => <div key={i}>{row}</div>)}
        </div>
      );
    };

    const renderCompactSpecs = () => {
      if (assetType === 'helicopter' && helicopterData) {
        const equipCount = helicopterData.selectedEquipment?.length || 0;
        if (isBuyRequest) {
          return (
            <SpecFrame rows={[
              helicopterData.maxAge != null && <SpecRow label="Max age" value={`${helicopterData.maxAge}y`} primary />,
              helicopterData.maxFlightHours != null && <SpecRow label="Max hrs" value={`${helicopterData.maxFlightHours.toLocaleString()}h`} />,
              helicopterData.urgencyLevel && <SpecRow label="Urgency" value={<span className="capitalize">{helicopterData.urgencyLevel}</span>} />,
            ]} />
          );
        }
        return (
          <SpecFrame rows={[
            helicopterData.airframeHours != null && <SpecRow label="Airframe" value={`${helicopterData.airframeHours.toLocaleString()}h`} icon={<Clock className={specIcon} />} primary />,
            helicopterData.engineHours != null && <SpecRow label="Engine" value={`${helicopterData.engineHours.toLocaleString()}h`} icon={<Gauge className={specIcon} />} />,
            year ? <SpecRow label="Year" value={year} icon={<Calendar className={specIcon} />} /> : equipCount > 0 ? <SpecRow label="Equipment" value={`+${equipCount}`} /> : null,
          ]} />
        );
      }

      if (assetType === 'engine' && engineData) {
        return (
          <SpecFrame rows={[
            engineData.tsn != null && <SpecRow label="TSN" value={engineData.tsn.toLocaleString()} icon={<Clock className={specIcon} />} primary />,
            engineData.csn != null && <SpecRow label="CSN" value={engineData.csn.toLocaleString()} />,
            engineData.tso != null
              ? <SpecRow label="TSO" value={engineData.tso.toLocaleString()} />
              : condition ? <SpecRow label="Condition" value={<span className="capitalize">{condition}</span>} /> : null,
          ]} />
        );
      }

      if (assetType === 'parts' && partsData) {
        const mfr = partsData.helicopterManufacturer || partsData.engineManufacturer || partsData.manufacturer;
        const mdl = partsData.helicopterModel || partsData.engineModel;
        return (
          <SpecFrame rows={[
            mfr ? <SpecRow label="Maker" value={mfr} primary /> : (partsData.itemType ? <SpecRow label="Type" value={<span className="capitalize">{partsData.itemType}</span>} primary /> : null),
            mdl && <SpecRow label="Model" value={mdl} />,
            partsData.partNumber
              ? <SpecRow label="P/N" value={<span className="font-mono text-[10px]">{partsData.partNumber}</span>} />
              : (mfr && partsData.itemType ? <SpecRow label="Type" value={<span className="capitalize">{partsData.itemType}</span>} /> : null),
          ]} />
        );
      }

      if (assetType === 'jobs' && jobData) {
        return (
          <SpecFrame rows={[
            jobData.companyName
              ? <SpecRow label="Company" value={jobData.companyName} icon={<Building2 className={specIcon} />} primary />
              : (jobData.jobCategory ? <SpecRow label="Role" value={<span className="capitalize">{jobData.jobCategory}</span>} primary /> : null),
            jobData.employmentType && <SpecRow label="Type" value={<span className="capitalize">{jobData.employmentType}</span>} />,
            jobData.typeRatings?.[0]
              ? <SpecRow label="Rating" value={jobData.typeRatings[0]} />
              : (jobData.jobCategory && jobData.companyName ? <SpecRow label="Role" value={<span className="capitalize">{jobData.jobCategory}</span>} /> : null),
          ]} />
        );
      }

      if (assetType === 'services' && servicesData) {
        return (
          <SpecFrame rows={[
            servicesData.category && <SpecRow label="Category" value={<span className="capitalize">{servicesData.category}</span>} primary />,
            servicesData.responseTime && <SpecRow label="Response" value={formatResponseTime(servicesData.responseTime)} icon={<Clock className={specIcon} />} />,
            servicesData.aogSupport
              ? <SpecRow label="AOG" value="Available" />
              : servicesData.shiftCoverage ? <SpecRow label="Coverage" value={formatShiftCoverage(servicesData.shiftCoverage)} /> : null,
          ]} />
        );
      }

      return <SpecFrame rows={[]} />;
    };

    return (
      <Card
        onClick={onClick}
        className={cn(
          "group relative cursor-pointer overflow-hidden touch-pan-y rounded-2xl flex flex-col h-full",
          "border border-border/60 bg-card",
          "shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_16px_-8px_rgba(0,0,0,0.08)]",
          "transition-all duration-300 hover:border-primary/30 hover:shadow-[0_8px_24px_-8px_rgba(0,0,0,0.18)] active:scale-[0.99]",
          isSelected && "ring-2 ring-primary ring-offset-2 ring-offset-background",
          className
        )}
      >
        {/* Image */}
        <div className="relative aspect-[4/3] flex-shrink-0 overflow-hidden bg-muted/50">
          {renderCompactVisual()}

          {/* Subtle top gradient for badge legibility */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-black/30 to-transparent" />

          {/* Top-left: type pin + featured */}
          <div className="absolute left-1.5 top-1.5 z-20 flex max-w-[calc(100%-3rem)] flex-col items-start gap-1">
            {(assetType === 'jobs' || assetType === 'services') && (
              <div className="flex h-5 w-5 sm:h-6 sm:w-6 items-center justify-center rounded-full border border-white/60 bg-white/95 shadow-sm">
                <img
                  src={assetType === 'jobs' ? getJobTypeMapPin(listingType) : getServiceTypeMapPin(listingType)}
                  alt=""
                  className="h-3 w-3 sm:h-3.5 sm:w-3.5"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
            )}
            {assetType !== 'services' ? (
              <ListingTypeBadge
                listingType={listingType}
                itemType={partsData?.itemType}
                specificityType={partsData?.specificityType}
                className="max-w-full truncate px-1.5 py-[2px] text-[8.5px] font-semibold shadow-sm"
              />
            ) : servicesData?.category ? (() => {
              const badgeCfg = getServiceCategoryBadgeConfig(servicesData.category);
              return <Badge className={cn("max-w-full truncate border-0 px-1.5 py-[2px] text-[8.5px] font-semibold shadow-sm", badgeCfg.badgeColor)}>{badgeCfg.label}</Badge>;
            })() : null}
            {renderStatusBadge()}
            {featured && (
              <span className="inline-flex items-center gap-0.5 rounded-md border-0 bg-accent/95 px-1.5 py-[2px] text-[8.5px] font-semibold text-accent-foreground shadow-sm">
                <Crown className="h-2 w-2 fill-current" />
                Featured
              </span>
            )}
          </div>

          {/* Top-right: photo count (tiny glass pill) */}
          {totalImages > 0 && (
            <span className="absolute right-1.5 top-1.5 z-20 inline-flex items-center gap-0.5 rounded-md bg-black/55 px-1 py-[2px] text-[8.5px] font-medium text-white backdrop-blur-sm">
              <Camera className="h-2.5 w-2.5" />
              {totalImages}
            </span>
          )}

          {/* Bottom-right: favorite + share — perfectly centered micro-circles */}
          {!isManagement && (
            <div className="absolute bottom-1.5 right-1.5 z-20 flex items-center gap-1">
              <button
                type="button"
                onClick={handleFavoriteClick}
                aria-label={isListingFavorite ? "Remove from favorites" : "Add to favorites"}
                style={{ minHeight: 0, minWidth: 0, padding: 0 }}
                className="grid h-6 w-6 place-items-center rounded-full border border-border/50 bg-background/90 text-muted-foreground shadow-sm backdrop-blur-md transition-all active:scale-90 hover:text-foreground"
              >
                <Heart className={cn("h-3 w-3 block", isListingFavorite && "fill-destructive text-destructive")} />
              </button>
              <ShareListingButton
                listingId={id}
                title={title}
                price={price}
                location={location}
                listingType={listingType}
                assetType={assetType}
                variant="ghost"
                size="icon"
                className="!h-6 !w-6 !min-h-0 !min-w-0 !p-0 grid place-items-center rounded-full border border-border/50 bg-background/90 text-muted-foreground shadow-sm backdrop-blur-md hover:bg-background hover:text-foreground [&_svg]:!h-3 [&_svg]:!w-3 [&_svg]:block"
              />
            </div>
          )}
        </div>

        {/* Content */}
        <CardContent className="flex min-h-0 flex-1 flex-col gap-2 p-2.5 sm:p-3">
          {/* Title */}
          <h3 className="text-[12.5px] sm:text-[13.5px] font-semibold leading-[1.25] tracking-tight text-foreground line-clamp-2 min-h-[2.5em]">
            {title}
          </h3>

          {/* Framed price box — fixed slot */}
          <div className="flex h-7 items-center">
            {renderInlinePrice()}
          </div>

          {/* Location row — always rendered at fixed height */}
          <div className="flex h-4 min-w-0 items-center gap-1 text-[10px] leading-none text-muted-foreground -mt-0.5">
            <MapPin className="h-2.5 w-2.5 flex-shrink-0" />
            {shouldHideLocation ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); if (!isOwner) requestField('location'); }}
                className="inline-flex items-center gap-1 truncate rounded border border-border/50 bg-muted/40 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
                aria-label="Request location"
              >
                <Lock className="h-2.5 w-2.5" />
                <span>Locked</span>
              </button>
            ) : location ? (
              <span className="truncate">{location}</span>
            ) : (
              <span className="truncate opacity-40">—</span>
            )}
          </div>

          {/* Structured spec matrix — always exactly 3 rows = identical pixel height */}
          <div>
            {renderCompactSpecs()}
          </div>

          {/* CTA — premium blue bar */}
          <div className="mt-auto flex items-center gap-1.5 pt-1">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onViewDetails?.(); }}
              style={{ minHeight: 0 }}
              className="group/cta inline-flex flex-1 items-center justify-center gap-1 rounded-lg bg-primary py-2 text-[10.5px] font-semibold uppercase tracking-wider text-primary-foreground shadow-sm transition-all hover:bg-primary/90 active:scale-[0.98]"
            >
              View details
              <Eye className="h-3 w-3 transition-transform group-hover/cta:translate-x-0.5" />
            </button>
            {sellerId && (
              <button
                type="button"
                onClick={handleMessageSeller}
                aria-label="Message seller"
                style={{ minHeight: 0, minWidth: 0 }}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border/60 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <MessageCircle className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Vertical Grid View Layout - 620px with fixed sections
  return (
    <Card 
      onClick={onClick}
      className={cn(
        "group cursor-pointer overflow-hidden touch-pan-y flex flex-col",
        "border border-border/40 bg-card",
        "shadow-sm hover:shadow-xl",
        "transition-all duration-300",
        "hover:border-primary/20",
        isManagement ? "h-auto min-h-[620px]" : "h-[620px]",
        isSelected && "ring-[6px] ring-amber-500 ring-offset-0 shadow-[0_0_30px_rgba(245,158,11,0.5)] bg-amber-50/30 dark:bg-amber-900/20 scale-[1.02] z-10",
        className
      )}
    >
      {/* Image Section - 200px - Smart handling for jobs and services (company logos use object-contain) */}
      <div className="relative bg-muted h-[200px] flex-shrink-0">
        {assetType === 'jobs' ? (
          // Jobs: For job posts/contracts, always use contain with neutral background for logos
          // For job profiles/seeking, use cover for personal photos
          (() => {
            const isCompanyListing = listingType === 'job_post' || listingType === 'job_contract';
            const imageUrl = jobData?.heroImage || jobData?.companyLogo;
            
            if (!imageUrl) {
              // No image - fallback
              return <BrandedListingFallback />;
            }
            
            // Company listings (job_post, job_contract) - always use contain for logos
            if (isCompanyListing) {
              return (
                <SmartLogo
                  src={imageUrl}
                  alt={jobData?.companyName || title}
                  variant="card"
                />
              );
            }
            
            // Personal listings (job_profile, job_seeking) - circular profile photo
            return (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-800 dark:to-slate-900">
                <div className="w-28 h-28 rounded-full overflow-hidden ring-4 ring-white dark:ring-slate-700 shadow-xl">
                  <img
                    src={imageUrl}
                    alt={title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = heroPlaceholder;
                    }}
                  />
                </div>
              </div>
            );
          })()
        ) : assetType === 'services' ? (
          // Services: Providers use contain for logos, Requests use cover
          (() => {
            const isProviderListing = listingType === 'service_offer' || listingType === 'capability_listing';
            const imageUrl = servicesData?.companyLogo || images?.[0];
            
            if (!imageUrl) {
              return <BrandedListingFallback />;
            }
            
            if (isProviderListing) {
              return (
                <SmartLogo
                  src={imageUrl}
                  alt={servicesData?.companyName || title}
                  variant="card"
                />
              );
            }
            
            return (
              hasPrimaryImage ? (
                <OptimizedImage
                  src={primaryImage}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <BrandedListingFallback />
              )
            );
          })()
        ) : (
          hasPrimaryImage ? (
            <OptimizedImage
              src={primaryImage}
              alt={title}
              className="w-full h-full object-cover"
            />
          ) : (
            <BrandedListingFallback />
          )
        )}
        
        {/* Job Type Pin Icon - Left Side for Jobs */}
        {assetType === 'jobs' && (
          <div className="absolute top-2 left-2 z-20">
            <div className={cn(
              "w-10 h-10 rounded-full bg-white/95 dark:bg-slate-800/95 shadow-lg flex items-center justify-center",
              isJobOffering(listingType) ? "ring-2 ring-emerald-400" : "ring-2 ring-amber-400"
            )}>
              <img src={getJobTypeMapPin(listingType)} alt="" className="w-7 h-7" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </div>
          </div>
        )}
        
        {/* Service Type Pin Icon + Category Badge - Left Side for Services */}
        {assetType === 'services' && (
          <div className="absolute top-2 left-2 z-20 flex items-center gap-1.5">
            <div className={cn(
              "w-10 h-10 rounded-full bg-white/95 dark:bg-slate-800/95 shadow-lg flex items-center justify-center",
              isServiceOffering(listingType) ? "ring-2 ring-emerald-400" : "ring-2 ring-amber-400"
            )}>
              <img src={getServiceTypeMapPin(listingType)} alt="" className="w-7 h-7" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </div>
            {/* Category Badge */}
            {servicesData?.category && (() => {
              const badgeCfg = getServiceCategoryBadgeConfig(servicesData.category);
              return (
                <Badge className={cn("text-[10px] px-2 py-0.5 shadow-lg font-bold border-0", badgeCfg.badgeColor)}>
                  {badgeCfg.label}
                </Badge>
              );
            })()}
          </div>
        )}
        
        {/* Left Side Badges - offset when job/service pin is present */}
        <div className={cn(
          "absolute flex flex-col gap-1.5 z-10",
          assetType === 'jobs' ? "top-14 left-2" : 
          assetType === 'services' ? "top-14 left-2" : 
          "top-2 left-2"
        )}>
          {assetType !== 'services' && (
            <ListingTypeBadge listingType={listingType} itemType={partsData?.itemType} specificityType={partsData?.specificityType} className="shadow-lg backdrop-blur-md text-xs" />
          )}
          {renderStatusBadge()}
          
          {featured && (
            <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-bold shadow-lg backdrop-blur-md text-xs">
              <Crown className="w-3 h-3 mr-1 fill-current" />
              Featured
            </Badge>
          )}
        </div>

        {/* Right Side - Photo Count */}
        {totalImages > 0 && (
          <Badge className="absolute top-2 right-2 bg-black/80 backdrop-blur-sm text-white border-0 shadow-lg px-2 py-0.5 font-medium text-xs">
            📷 {totalImages}
          </Badge>
        )}

        {/* Action Buttons - Bottom Right Corner (always visible on mobile, hover on desktop) */}
        {!isManagement && (
          <div className="absolute bottom-2 right-2 flex gap-1.5 z-10 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300">
            <Button 
              size="sm" 
              variant="secondary" 
              className="w-8 h-8 p-0 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm shadow-lg hover:bg-white dark:hover:bg-slate-800 hover:scale-110 transition-all"
              aria-label={isListingFavorite ? "Remove from favorites" : "Add to favorites"}
              onClick={handleFavoriteClick}
            >
              <Heart className={cn(
                "w-4 h-4 transition-all",
                isListingFavorite 
                  ? "fill-red-500 text-red-500" 
                  : "text-slate-700"
              )} />
            </Button>
            <ShareListingButton
              listingId={id}
              title={title}
              price={price}
              location={location}
              listingType={listingType}
              assetType={assetType}
              variant="ghost"
              size="icon"
              className="w-8 h-8 p-0 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm shadow-lg hover:bg-white dark:hover:bg-slate-800 hover:scale-110 transition-all"
            />
          </div>
        )}
      </div>

      <CardContent className="flex-1 flex flex-col p-4 overflow-hidden">
        {/* Header Section - 80px fixed */}
        <div className="h-[80px] flex-shrink-0 border-b border-border/30 pb-3 mb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-sm leading-tight line-clamp-2 mb-1">{title}</h3>
              {/* Year & Condition inline */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {year && <span className="font-medium">{year}</span>}
                {condition && (
                  <Badge variant="outline" className="text-[10px] capitalize h-5 px-1.5 bg-slate-50 dark:bg-slate-800">
                    {condition}
                  </Badge>
                )}
              </div>
            </div>
            {/* Price with professional styling - intent-based colors */}
            <div className={cn(
              "text-right shrink-0 px-3 py-1.5 rounded-lg shadow-sm",
              isBuyRequest 
                ? "bg-gradient-to-br from-[hsl(45,60%,97%)] to-[hsl(45,50%,93%)] dark:from-[hsl(45,40%,15%)] dark:to-[hsl(45,30%,10%)] border border-[hsl(45,70%,80%)]/60 dark:border-[hsl(45,50%,30%)]/40"
                : getFormattedPrice.displayType === 'quote'
                  ? "bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/50 dark:to-blue-900/30 border border-blue-200/60 dark:border-blue-700/40"
                  : getFormattedPrice.displayType === 'contact'
                    ? "bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-900/30 border border-slate-200/60 dark:border-slate-700/40"
                    : assetType === 'services' && getFormattedPrice.displayType === 'price'
                      ? "bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/50 dark:to-green-900/30 border border-emerald-200/60 dark:border-emerald-700/40"
                      : assetType === 'jobs' && getFormattedPrice.showPrice
                        ? "bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/50 dark:to-indigo-900/30 border border-blue-200/60 dark:border-blue-700/40"
                        : "bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-900/30 border border-slate-200/60 dark:border-slate-700/40"
            )}>
              {shouldHidePrice ? (
                <BlurredPrivateData
                  placeholderContent={
                    <div className={cn(
                      "font-bold text-base leading-tight",
                      isBuyRequest 
                        ? "text-[hsl(45,60%,32%)] dark:text-[hsl(45,70%,65%)]"
                        : "text-slate-800 dark:text-slate-200"
                    )}>
                      $XX,XXX{isBuyRequest ? '' : '/mo'}
                    </div>
                  }
                  fieldName="Price"
                  size="sm"
                  inline
                  onRequestInfo={isOwner ? undefined : () => requestField('price')}
                />
              ) : getFormattedPrice.displayType === 'quote' ? (
                <div className="flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                  <span className="font-semibold text-sm text-blue-700 dark:text-blue-300">Request Quote</span>
                </div>
              ) : getFormattedPrice.displayType === 'contact' ? (
                <span className="text-xs text-muted-foreground italic">Contact for Pricing</span>
              ) : getFormattedPrice.displayType === 'none' && assetType === 'jobs' ? (
                <span className="text-sm text-muted-foreground">Competitive</span>
              ) : (
                <>
                  {isBuyRequest && <span className="text-[9px] text-[hsl(45,55%,42%)] dark:text-[hsl(45,60%,60%)] block uppercase tracking-wide font-medium">Budget</span>}
                  {assetType === 'jobs' && !isBuyRequest && getFormattedPrice.showPrice && <span className="text-[9px] text-blue-600 dark:text-blue-400 block uppercase tracking-wide font-medium">Salary</span>}
                  {assetType === 'services' && getFormattedPrice.displayType === 'price' && <span className="text-[9px] text-emerald-600 dark:text-emerald-400 block uppercase tracking-wide font-medium">Rate</span>}
                  <div className={cn(
                    "font-bold text-base leading-tight",
                    isBuyRequest 
                      ? "text-[hsl(45,60%,32%)] dark:text-[hsl(45,70%,65%)]"
                      : assetType === 'services' && getFormattedPrice.displayType === 'price'
                        ? "text-emerald-700 dark:text-emerald-400"
                        : assetType === 'jobs' && getFormattedPrice.showPrice
                          ? "text-blue-700 dark:text-blue-300"
                          : "text-slate-800 dark:text-slate-200"
                  )}>
                    {formattedPrice}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Location - single line with truncate to prevent overlap */}
          {location && !shouldHideLocation && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2 max-w-full overflow-hidden">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{location}</span>
              {/* Not on map indicator */}
              {!hasCoordinates && (
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 ml-1 flex-shrink-0 bg-amber-50/80 dark:bg-amber-900/20 border-amber-200/60 dark:border-amber-700/40 text-amber-600 dark:text-amber-400">
                  Not on map
                </Badge>
              )}
            </div>
          )}
          {!location && !shouldHideLocation && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2 max-w-full overflow-hidden">
              <MapPin className="w-3 h-3 flex-shrink-0 text-amber-500" />
              <span className="truncate italic text-amber-600 dark:text-amber-400">Location not set</span>
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 ml-1 flex-shrink-0 bg-amber-50/80 dark:bg-amber-900/20 border-amber-200/60 dark:border-amber-700/40 text-amber-600 dark:text-amber-400">
                Not on map
              </Badge>
            </div>
          )}
          {shouldHideLocation && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2 max-w-full overflow-hidden">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <BlurredPrivateData
                placeholderContent={<span className="truncate">City, Country</span>}
                fieldName="Location"
                size="sm"
                inline
                onRequestInfo={isOwner ? undefined : () => requestField('location')}
              />
            </div>
          )}
        </div>

        {/* Specs Section - clipped inside fixed card, page handles vertical touch scroll */}
        <div className="flex-1 min-h-0 overflow-hidden touch-pan-y">
          {renderSpecs()}
        </div>

        {/* Action Buttons - Fixed footer 60px */}
        <div className="h-[60px] flex-shrink-0 pt-3">
          {isManagement ? renderManagementActions() : renderDefaultActions()}
        </div>
      </CardContent>
    </Card>
  );
};

// Memoized component to prevent unnecessary re-renders
export const MarketplaceListingCard = memo(MarketplaceListingCardComponent, (prevProps, nextProps) => {
  // Custom comparison - only re-render if key props change
  return (
    prevProps.id === nextProps.id &&
    prevProps.title === nextProps.title &&
    prevProps.price === nextProps.price &&
    prevProps.location === nextProps.location &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.listingStatus === nextProps.listingStatus &&
    prevProps.featured === nextProps.featured &&
    prevProps.variant === nextProps.variant &&
    prevProps.images?.[0] === nextProps.images?.[0]
  );
});

MarketplaceListingCard.displayName = 'MarketplaceListingCard';

export default MarketplaceListingCard;
